import { useState } from "react";
import { 
  Building2, Shield, Gavel, Sparkles, Phone, Mail, ChevronRight,
  Zap, BarChart3, Clock, Activity, Wrench, Gauge, CheckCircle2,
  AlertTriangle, DollarSign, FileText, Eye, AudioWaveform, TrendingUp,
  Target, Users, ArrowRight, Play, Info, ShieldCheck, Car, XCircle,
  TrendingDown, AlertOctagon, Lock, Scale, LineChart, PieChart, Globe,
  ChevronDown, Gift, Cog, FileCheck, Banknote
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ============ COUNTRY & MARKET DATA ============
// Exchange rates: 3-month average (Sep-Nov 2025)
// Sources: Mordor Intelligence, TechSci Research, Nexdigm, Ken Research, BOT, IMF, ADB, IRDAI, PIAM

const COUNTRY_DATA = {
  india: {
    name: "India",
    flag: "🇮🇳",
    currency: "INR",
    currencySymbol: "₹",
    exchangeRate: 84.2, // 3-month avg: Sep-Nov 2025
    market: {
      // Total auto loan market: USD 43.45B (2025), used car segment: ~USD 8.77B
      // Source: Mordor Intelligence, TechSci Research
      usedCarLoanMarket: 8.77, // Billion USD - Used car loan segment specifically
      totalAutoLoanMarket: 43.45, // Billion USD - Total auto (new + used)
      projectedMarket2030: 18.04, // Used car loan by 2029/2030
      cagr: 12.83,
      nplRate: 6.0, // Mid-tier NBFCs: 6%+, Banks: sub-2%, weighted avg ~6% for used
      financingPenetration: 80, // 80%+ of cars financed per research
      avgLoanSize: 8500, // USD
      totalNPLExposure: 526, // Million USD (8.77B * 6%)
      fraudExposure: 700, // Million USD annual - odometer fraud, inspection manipulation
      avgRecoveryRate: 62, // Percentage
    },
    // Banking insights for B2B Banks section
    insights: {
      headline: "Fastest Growing Used Car Finance Market in Asia",
      keyStats: [
        "Total auto loan market: USD 43.45B (2025)",
        "Used car loan segment: USD 8.77B → USD 18.04B by 2030",
        "80%+ financing penetration for vehicle purchases",
        "Vehicle-loan GNPA exceeds 6% for mid-tier NBFCs"
      ],
      riskFactors: [
        "Mid-tier NBFCs: 6%+ GNPA vs sub-2% for leading banks",
        "Odometer tampering undetected in 35% of cases",
        "EMIs for entry-level cars exceed ₹10,000/month",
        "Mechanic report manipulation prevalent in tier-2/3 cities"
      ]
    },
    // Insurance market data - Source: Mordor Intelligence, IRDAI, ResearchAndMarkets
    insurance: {
      motorInsuranceMarket: 11.39, // Billion USD (2024)
      projectedMarket2030: 21.48, // Billion USD
      cagr: 10.25,
      claimsRatio: 82.88, // ICR - Incurred Claims Ratio
      combinedRatioPSU: 125, // PSU insurers - underwriting loss
      combinedRatioPrivate: 105, // Private insurers
      fraudRate: 16, // Industry estimates 15-20% of claims involve fraud elements
      fraudExposure: 1822, // Million USD (16% of 11.39B)
      avgPremium: 285, // USD per policy
      thirdPartyShare: 66.34, // % of market
      comprehensiveShare: 33.66, // % of market
      registeredVehicles: 350, // Million vehicles
      insuredVehicles: 180, // Million - ~51% penetration
      newPoliciesPerYear: 45, // Million new policies annually
    },
    insuranceInsights: {
      headline: "USD 11.39B Market with 16% Fraud Rate - AI Inspection Opportunity",
      keyStats: [
        "Motor insurance market: USD 11.39B (2024) → USD 21.48B (2030)",
        "350M+ registered vehicles, only 51% insured",
        "Third-party policies dominate at 66.34% share",
        "PSU insurers: 125% combined ratio (underwriting loss)"
      ],
      painPoints: [
        "16% of claims involve fraud elements (USD 1.82B annual)",
        "Manual inspections miss 40% of pre-existing damage",
        "Staged accidents and inflated claims rising 22% YoY",
        "No engine condition data in standard pre-inspection"
      ]
    },
    extendedWarrantyMarket: {
      marketSize: 4.4, // Billion USD (2025) - all products
      autoShare: 35, // % of extended warranty is automotive
      autoMarketSize: 1.54, // Billion USD
      projectedMarket2034: 10.04,
      cagr: 9.6,
      penetration: 12, // % of car buyers opt for extended warranty
    },
    // Most popular used car for financing in India
    popularVehicle: {
      make: "Maruti Suzuki",
      model: "Swift VXi",
      year: 2021,
      odometer: 45000,
      bookValueUSD: 7800, // ~₹6.5L for 3-year-old Swift
      trueValueUSD: 7020,
      avgLoanAmount: 6500, // USD - typical 80% LTV
      engineIssue: "Timing Chain Wear",
      engineDeduction: 540,
      bodyIssue: "3 impact points",
      bodyDeduction: 240,
    },
    warrantyPricing: {
      annualCostUSD: 180, // ~₹15,000/year in India
      typicalLoanTenure: 3, // years
    },
  },
  thailand: {
    name: "Thailand",
    flag: "🇹🇭",
    currency: "THB",
    currencySymbol: "฿",
    exchangeRate: 34.5, // 3-month avg: Sep-Nov 2025
    market: {
      // Total automotive financing: USD 59.8B (2024), auto finance segment: ~USD 10-15B
      // Source: Astute Analytica, Nexdigm, BOT
      usedCarLoanMarket: 15.0, // Billion USD - Car finance & leasing
      totalAutoLoanMarket: 59.8, // Billion USD - Total automotive financing
      projectedMarket2030: 87.8, // By 2033
      cagr: 4.36,
      nplRate: 15.69, // Special mention loan ratio for auto Q3 2024 - highest in ASEAN
      financingPenetration: 68, // 68% via traditional loans
      avgLoanSize: 12000,
      totalNPLExposure: 2354, // Million USD (15B * 15.69%)
      fraudExposure: 450, // Million USD annual
      avgRecoveryRate: 52, // Lower due to EV disruption
    },
    // Banking insights for B2B Banks section
    insights: {
      headline: "Highest Auto NPL in ASEAN - 15.69% Special Mention Ratio",
      keyStats: [
        "Total automotive financing: USD 59.8B (2024)",
        "Auto loan special mention ratio: 15.69% (Q3 2024)",
        "Auto loan defaults surged 32% YoY in early 2024",
        "Household debt at 88.4% of GDP pressures repayment"
      ],
      riskFactors: [
        "Highest special mention loan ratio among all categories",
        "Government 'Khun Soo, Rao Chuay' debt restructuring active",
        "EV adoption disrupting traditional vehicle valuations",
        "Banks raised lending rates ~70bps on vehicle financing"
      ]
    },
    // Insurance market data - Source: Mordor Intelligence, VMR, TechSci
    insurance: {
      motorInsuranceMarket: 4.78, // Billion USD (2024)
      projectedMarket2030: 7.04, // Billion USD
      cagr: 6.22,
      claimsRatio: 68, // Estimated
      combinedRatioPSU: 98, // Thai insurers generally profitable
      combinedRatioPrivate: 95,
      fraudRate: 12, // Industry estimates 10-15% fraud involvement
      fraudExposure: 574, // Million USD (12% of 4.78B)
      avgPremium: 380, // USD per policy
      thirdPartyShare: 45, // Voluntary comprehensive higher
      comprehensiveShare: 55,
      registeredVehicles: 44.8, // Million vehicles
      insuredVehicles: 38, // Million - ~85% penetration
      newPoliciesPerYear: 12, // Million new policies annually
    },
    insuranceInsights: {
      headline: "USD 4.78B Market with 12% Fraud Rate - EV Disruption Risk",
      keyStats: [
        "Motor insurance: USD 4.78B (2024) at 6.22% CAGR",
        "44.8M registered vehicles, 85% insurance penetration",
        "Cars dominate at 92.96% of motor insurance market",
        "53.8% of general insurance GWP is motor"
      ],
      painPoints: [
        "12% of claims involve fraud elements (USD 574M annual)",
        "EV adoption disrupting traditional IDV models",
        "High household debt (88.4% GDP) increases claim fraud risk",
        "New-car registrations down 26% in 2024 - used car focus"
      ]
    },
    extendedWarrantyMarket: {
      marketSize: 0.85, // Billion USD
      autoShare: 40,
      autoMarketSize: 0.34,
      projectedMarket2034: 1.8,
      cagr: 8.5,
      penetration: 18,
    },
    // Most popular used car for financing in Thailand
    popularVehicle: {
      make: "Toyota",
      model: "Vios E",
      year: 2021,
      odometer: 55000,
      bookValueUSD: 11500, // ~฿400K for 3-year-old Vios
      trueValueUSD: 10200,
      avgLoanAmount: 9200, // USD - typical 80% LTV
      engineIssue: "Valve Train Noise",
      engineDeduction: 680,
      bodyIssue: "2 panel repairs",
      bodyDeduction: 320,
    },
    warrantyPricing: {
      annualCostUSD: 280, // ~฿9,500/year in Thailand
      typicalLoanTenure: 4, // years - longer tenure common
    },
  },
  malaysia: {
    name: "Malaysia",
    flag: "🇲🇾",
    currency: "MYR",
    currencySymbol: "RM",
    exchangeRate: 4.42, // 3-month avg: Sep-Nov 2025
    market: {
      // Auto finance market: USD 2.6B (2024), broader automotive financing higher
      // Source: Nexdigm, ADB
      usedCarLoanMarket: 2.6, // Billion USD - Auto finance core
      totalAutoLoanMarket: 15.8, // Billion USD - Including hire purchase
      projectedMarket2030: 4.2, // By 2030 at 4.5% CAGR
      cagr: 4.5,
      nplRate: 1.4, // Overall banking GIL ratio - lowest in region
      financingPenetration: 85, // 85%+ via formal loans
      avgLoanSize: 15000,
      totalNPLExposure: 36, // Million USD (2.6B * 1.4%)
      fraudExposure: 120, // Million USD annual
      avgRecoveryRate: 78, // Highest in region
    },
    // Banking insights for B2B Banks section
    insights: {
      headline: "Lowest NPL Rate in ASEAN - Strong Regulatory Framework",
      keyStats: [
        "Banking GIL ratio: 1.4% (July 2025) - record low",
        "85%+ vehicles financed via formal channels",
        "830,000 vehicles sold in 2024, 85% financed",
        "Loan tenures extended to 7-9 years amid rising prices"
      ],
      riskFactors: [
        "Used car segment carries higher risk (est. 2.8% NPL)",
        "Household debt-to-GDP at 84.8%",
        "National vs non-national car value gap creates arbitrage",
        "Grey import vehicles challenge accurate valuation"
      ]
    },
    // Insurance market data - Source: PIAM, Mordor Intelligence, TechSci
    insurance: {
      motorInsuranceMarket: 2.72, // Billion USD (2025)
      projectedMarket2030: 3.98, // Billion USD
      cagr: 7.39,
      claimsRatio: 102.2, // Combined ratio H1 2025 - slight underwriting loss
      combinedRatioPSU: 102,
      combinedRatioPrivate: 99,
      fraudRate: 10, // Industry estimates 8-12% fraud involvement
      fraudExposure: 272, // Million USD (10% of 2.72B)
      avgPremium: 420, // USD per policy - higher premiums
      thirdPartyShare: 22, // Comprehensive dominates
      comprehensiveShare: 78, // 78.10% comprehensive
      registeredVehicles: 32, // Million vehicles
      insuredVehicles: 28, // Million - 87.5% penetration
      newPoliciesPerYear: 8, // Million new policies annually
    },
    insuranceInsights: {
      headline: "USD 2.72B Market with 10% Fraud Rate - Premium Market Opportunity",
      keyStats: [
        "Motor GWP: RM 5.3B (H1 2025), 42.8% of general insurance",
        "78.10% comprehensive policies - highest in region",
        "Personal vehicles: 73.55% of written premiums",
        "5.7% YoY growth in motor insurance H1 2025"
      ],
      painPoints: [
        "10% of claims involve fraud elements (USD 272M annual)",
        "Combined ratio 102.2% - underwriting loss",
        "Claims frequency rising from flood events",
        "Grey imports and national car value gaps"
      ]
    },
    extendedWarrantyMarket: {
      marketSize: 0.65, // Billion USD
      autoShare: 45,
      autoMarketSize: 0.29,
      projectedMarket2034: 1.4,
      cagr: 8.8,
      penetration: 22, // Higher due to comprehensive culture
    },
    // Most popular used car for financing in Malaysia
    popularVehicle: {
      make: "Perodua",
      model: "Myvi 1.5 AV",
      year: 2021,
      odometer: 48000,
      bookValueUSD: 13200, // ~RM58K for 3-year-old Myvi AV
      trueValueUSD: 11900,
      avgLoanAmount: 10500, // USD - typical 80% LTV
      engineIssue: "CVT Hesitation",
      engineDeduction: 720,
      bodyIssue: "Minor dents, paint fade",
      bodyDeduction: 280,
    },
    warrantyPricing: {
      annualCostUSD: 320, // ~RM1,400/year in Malaysia
      typicalLoanTenure: 5, // years - 9-year max common
    },
  },
  philippines: {
    name: "Philippines",
    flag: "🇵🇭",
    currency: "PHP",
    currencySymbol: "₱",
    exchangeRate: 56.8, // 3-month avg: Sep-Nov 2025
    market: {
      // Outstanding auto loans: PHP 572B (~USD 10.1B), market valued at PHP 650B
      // Source: Ken Research, Statista, Nexdigm
      usedCarLoanMarket: 10.1, // Billion USD - Outstanding auto loans (Dec 2024)
      totalAutoLoanMarket: 11.4, // Billion USD - Total market value
      projectedMarket2030: 14.8, // By 2029 at 8% CAGR
      cagr: 8.0,
      nplRate: 5.5, // Auto loan specific NPL rate
      financingPenetration: 65, // Commercial banks hold 65% share
      avgLoanSize: 9500,
      totalNPLExposure: 555, // Million USD (10.1B * 5.5%)
      fraudExposure: 180, // Million USD annual
      avgRecoveryRate: 54, // Lower due to informal trading
    },
    // Banking insights for B2B Banks section
    insights: {
      headline: "Motor Vehicle Loans Grew 18.1% YoY - Emerging Opportunity",
      keyStats: [
        "Outstanding auto loans: PHP 572B / USD 10.1B (Dec 2024)",
        "MVL expanded 18.1% YoY to PHP 568.1B (Jul 2024)",
        "Used vehicle loans growing fastest at 9.4% CAGR",
        "467,252 vehicles sold in 2024, up 8.7% YoY"
      ],
      riskFactors: [
        "Auto loan NPL rate at 5.5% - above regional average",
        "NPLs grew 15.5% YoY, outpacing loan growth",
        "Limited used car financing infrastructure",
        "Informal vehicle trading complicates collateral verification"
      ]
    },
    // Insurance market data - Source: Insurance Commission PH, VMR, Mordor
    insurance: {
      motorInsuranceMarket: 2.31, // Billion USD (2025)
      projectedMarket2030: 7.6, // Billion USD by 2032
      cagr: 10.5,
      claimsRatio: 72, // Estimated
      combinedRatioPSU: 95,
      combinedRatioPrivate: 92,
      fraudRate: 14, // Industry estimates 12-18% fraud involvement
      fraudExposure: 323, // Million USD (14% of 2.31B)
      avgPremium: 195, // USD per policy - lower premiums
      thirdPartyShare: 63.5, // CTPL dominates
      comprehensiveShare: 36.5,
      registeredVehicles: 14.5, // Million vehicles
      insuredVehicles: 8.2, // Million - 56.5% penetration
      newPoliciesPerYear: 3, // Million new policies annually
    },
    insuranceInsights: {
      headline: "USD 2.31B Market with 14% Fraud Rate - High Growth Opportunity",
      keyStats: [
        "Motor NPW: PHP 28.86B (2024), +6.28% YoY",
        "Motor is 40% of non-life insurance premiums",
        "H1 2025 motor NPW: PHP 16.06B (+16.20% YoY)",
        "CTPL benefits increased March 2024 - no premium hike"
      ],
      painPoints: [
        "14% of claims involve fraud elements (USD 323M annual)",
        "Only 56.5% insurance penetration - lowest in ASEAN",
        "Comprehensive coverage only 36.5% - underinsured",
        "Informal vehicle trading complicates claims verification"
      ]
    },
    extendedWarrantyMarket: {
      marketSize: 0.48, // Billion USD
      autoShare: 30,
      autoMarketSize: 0.14,
      projectedMarket2034: 1.2,
      cagr: 10.2,
      penetration: 8, // Low - big opportunity
    },
    // Most popular used car for financing in Philippines
    popularVehicle: {
      make: "Toyota",
      model: "Vios XLE",
      year: 2021,
      odometer: 52000,
      bookValueUSD: 9800, // ~₱550K for 3-year-old Vios
      trueValueUSD: 8700,
      avgLoanAmount: 7800, // USD - typical 80% LTV
      engineIssue: "Injector Deposits",
      engineDeduction: 580,
      bodyIssue: "Flood exposure signs",
      bodyDeduction: 420,
    },
    warrantyPricing: {
      annualCostUSD: 200, // ~₱11,000/year in Philippines
      typicalLoanTenure: 3, // years
    },
  }
};

// Sample vehicle data adjusted by country - uses popular vehicle for that market
const getSampleVehicleData = (country, showLocalCurrency = false) => {
  const data = COUNTRY_DATA[country];
  const vehicle = data.popularVehicle;
  
  // Get values in USD (base) and local currency
  const bookValueUSD = vehicle.bookValueUSD;
  const trueValueUSD = vehicle.trueValueUSD;
  const localBookValue = Math.round(bookValueUSD * data.exchangeRate);
  const localTrueValue = Math.round(trueValueUSD * data.exchangeRate);
  
  return {
    make: vehicle.make,
    model: vehicle.model,
    year: vehicle.year,
    odometer: vehicle.odometer,
    bookValueUSD,
    trueValueUSD,
    localBookValue,
    localTrueValue,
    avgLoanAmount: vehicle.avgLoanAmount,
    localLoanAmount: Math.round(vehicle.avgLoanAmount * data.exchangeRate),
    engineIssue: vehicle.engineIssue,
    deductionEngine: vehicle.engineDeduction,
    localDeductionEngine: Math.round(vehicle.engineDeduction * data.exchangeRate),
    bodyIssue: vehicle.bodyIssue,
    deductionBody: vehicle.bodyDeduction,
    localDeductionBody: Math.round(vehicle.bodyDeduction * data.exchangeRate),
    overvaluationRisk: bookValueUSD - trueValueUSD,
    localOvervaluationRisk: Math.round((bookValueUSD - trueValueUSD) * data.exchangeRate),
    overvaluationPercent: Math.round(((bookValueUSD - trueValueUSD) / bookValueUSD) * 100),
    // For display
    displayBookValue: showLocalCurrency ? localBookValue : bookValueUSD,
    displayTrueValue: showLocalCurrency ? localTrueValue : trueValueUSD,
    displayLoanAmount: showLocalCurrency ? Math.round(vehicle.avgLoanAmount * data.exchangeRate) : vehicle.avgLoanAmount,
    displayEngineDeduction: showLocalCurrency ? Math.round(vehicle.engineDeduction * data.exchangeRate) : vehicle.engineDeduction,
    displayBodyDeduction: showLocalCurrency ? Math.round(vehicle.bodyDeduction * data.exchangeRate) : vehicle.bodyDeduction,
    displayOvervaluation: showLocalCurrency ? Math.round((bookValueUSD - trueValueUSD) * data.exchangeRate) : (bookValueUSD - trueValueUSD),
  };
};

// Predictive value data generator - PORTFOLIO VALUE PROTECTION MODEL
// Shows how Wisedrive PROTECTS portfolio value vs Legacy model's value erosion
const getPortfolioValueData = (country, showLocalCurrency = false) => {
  const data = COUNTRY_DATA[country];
  const vehicle = data.popularVehicle;
  const bookValue = vehicle.bookValueUSD; // What legacy model thinks the car is worth
  const trueValue = vehicle.trueValueUSD; // Wisedrive's accurate starting value
  const rate = showLocalCurrency ? data.exchangeRate : 1;
  
  const applyRate = (val) => Math.round(val * rate);
  
  // Years 0-5 of loan tenure
  return {
    // Legacy Model: Bank THINKS they have book value, but ACTUAL value crashes
    // as hidden issues surface (timing chain fails, accident history discovered, etc.)
    legacyExpected: [
      { year: 0, value: applyRate(bookValue), label: "Disbursement" },
      { year: 1, value: applyRate(Math.round(bookValue * 0.88)), label: "Year 1" },
      { year: 2, value: applyRate(Math.round(bookValue * 0.76)), label: "Year 2" },
      { year: 3, value: applyRate(Math.round(bookValue * 0.65)), label: "Year 3" },
      { year: 4, value: applyRate(Math.round(bookValue * 0.55)), label: "Year 4" },
      { year: 5, value: applyRate(Math.round(bookValue * 0.46)), label: "Maturity" }
    ],
    // Legacy ACTUAL: Hidden issues surface, value crashes below expectations
    legacyActual: [
      { year: 0, value: applyRate(trueValue), label: "Disbursement" },  // Already overvalued!
      { year: 1, value: applyRate(Math.round(trueValue * 0.78)), label: "Year 1" },  // Engine issue surfaces
      { year: 2, value: applyRate(Math.round(trueValue * 0.58)), label: "Year 2" },  // Major repair needed
      { year: 3, value: applyRate(Math.round(trueValue * 0.45)), label: "Year 3" },  // Accident history discovered
      { year: 4, value: applyRate(Math.round(trueValue * 0.35)), label: "Year 4" },  // Multiple failures
      { year: 5, value: applyRate(Math.round(trueValue * 0.28)), label: "Maturity" } // Auction recovery: terrible
    ],
    // Wisedrive Model: Start with accurate value, PROTECT it through tenure
    wisedrive: [
      { year: 0, value: applyRate(trueValue), label: "Disbursement" },  // Accurate from day 1
      { year: 1, value: applyRate(Math.round(trueValue * 0.92)), label: "Year 1" },  // Issues fixed under warranty
      { year: 2, value: applyRate(Math.round(trueValue * 0.85)), label: "Year 2" },  // Protected depreciation
      { year: 3, value: applyRate(Math.round(trueValue * 0.78)), label: "Year 3" },  // Maintained condition
      { year: 4, value: applyRate(Math.round(trueValue * 0.72)), label: "Year 4" },  // Predictable curve
      { year: 5, value: applyRate(Math.round(trueValue * 0.67)), label: "Maturity" } // Strong auction recovery
    ],
    // Key metrics
    metrics: {
      legacyGapAtMaturity: applyRate(Math.round(bookValue * 0.46) - Math.round(trueValue * 0.28)), // Expected vs Actual gap
      wisedriveRecovery: applyRate(Math.round(trueValue * 0.67)),
      legacyRecovery: applyRate(Math.round(trueValue * 0.28)),
      valueProtected: applyRate(Math.round(trueValue * 0.67) - Math.round(trueValue * 0.28)),
      nplRiskReduction: 35, // Percentage
    }
  };
};

// ============ COMPONENTS ============

const CountrySelector = ({ selectedCountry, onCountryChange, showLocalCurrency, onCurrencyToggle }) => {
  const data = COUNTRY_DATA[selectedCountry];
  
  return (
    <div className="flex items-center gap-3">
      {/* Currency Toggle */}
      <div className="flex items-center gap-2 bg-white shadow-sm px-3 py-2 rounded-full border border-slate-200">
        <button
          onClick={() => onCurrencyToggle(false)}
          className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
            !showLocalCurrency 
              ? 'bg-[#003399] text-white shadow-sm' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="currency-usd"
        >
          $ USD
        </button>
        <button
          onClick={() => onCurrencyToggle(true)}
          className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
            showLocalCurrency 
              ? 'bg-[#003399] text-white shadow-sm' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="currency-local"
        >
          {data.currencySymbol} {data.currency}
        </button>
      </div>
      
      {/* Country Selector */}
      <div className="flex items-center gap-3 bg-white shadow-sm px-4 py-2 rounded-full border border-slate-200">
        <Globe size={18} className="text-slate-400" />
        <Select value={selectedCountry} onValueChange={onCountryChange}>
          <SelectTrigger className="w-[180px] bg-transparent border-0 text-slate-700 focus:ring-0" data-testid="country-selector">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white border-slate-200">
            {Object.entries(COUNTRY_DATA).map(([key, country]) => (
              <SelectItem key={key} value={key} className="text-slate-700 hover:bg-slate-50">
                <span className="flex items-center gap-2">
                  <span>{country.flag}</span>
                  <span>{country.name}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

const MarketIntelligencePanel = ({ country, showLocalCurrency }) => {
  const data = COUNTRY_DATA[country];
  
  // Format currency based on toggle
  const formatCurrency = (valueUSD, suffix = '') => {
    if (showLocalCurrency) {
      const localVal = Math.round(valueUSD * data.exchangeRate);
      return `${data.currencySymbol}${localVal.toLocaleString()}${suffix}`;
    }
    return `$${valueUSD.toLocaleString()}${suffix}`;
  };
  
  const formatBillions = (valueB) => {
    if (showLocalCurrency) {
      const localVal = valueB * data.exchangeRate;
      return `${data.currencySymbol}${localVal.toFixed(1)}B`;
    }
    return `$${valueB}B`;
  };
  
  const formatMillions = (valueM) => {
    if (showLocalCurrency) {
      const localVal = Math.round(valueM * data.exchangeRate);
      return `${data.currencySymbol}${localVal.toLocaleString()}M`;
    }
    return `$${valueM}M`;
  };
  
  return (
    <div className="mb-12 p-8 rounded-3xl bg-white border border-slate-200 shadow-xl" data-testid="market-intelligence">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
        {/* Left: Market Overview */}
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-4xl">{data.flag}</span>
            <div>
              <Badge className="bg-[#003399]/10 text-[#003399] border-[#003399]/20 mb-1">Market Intelligence</Badge>
              <h3 className="text-2xl font-bold text-slate-900">{data.name} Auto Finance Market</h3>
            </div>
          </div>
          
          <p className="text-lg text-amber-600 font-semibold mb-4">{data.insights.headline}</p>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Total Auto Loan Market</div>
              <div className="text-2xl font-bold text-slate-900">{formatBillions(data.market.totalAutoLoanMarket)}</div>
              <div className="text-xs text-emerald-600">Used Car Segment: {formatBillions(data.market.usedCarLoanMarket)}</div>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Auto Loan NPL Rate</div>
              <div className="text-2xl font-bold text-red-500">{data.market.nplRate}%</div>
              <div className="text-xs text-slate-500">Exposure: {formatMillions(data.market.totalNPLExposure)}</div>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Financing Penetration</div>
              <div className="text-2xl font-bold text-slate-900">{data.market.financingPenetration}%</div>
              <div className="text-xs text-slate-500">Avg. Loan: {formatCurrency(data.market.avgLoanSize)}</div>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Est. Fraud Exposure</div>
              <div className="text-2xl font-bold text-amber-500">{formatMillions(data.market.fraudExposure)}</div>
              <div className="text-xs text-slate-500">Recovery Rate: {data.market.avgRecoveryRate}%</div>
            </div>
          </div>
        </div>

        {/* Right: Key Insights */}
        <div className="lg:w-[400px] space-y-4">
          <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
            <div className="text-xs text-emerald-700 font-bold uppercase tracking-wider mb-2">Key Market Statistics</div>
            <ul className="space-y-2">
              {data.insights.keyStats.map((stat, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                  {stat}
                </li>
              ))}
            </ul>
          </div>
          
          <div className="p-4 bg-red-50 rounded-xl border border-red-200">
            <div className="text-xs text-red-700 font-bold uppercase tracking-wider mb-2">Risk Factors</div>
            <ul className="space-y-2">
              {data.insights.riskFactors.map((risk, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <AlertTriangle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
                  {risk}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const LegacyVsWisedriveComparison = ({ country, showLocalCurrency }) => {
  const data = COUNTRY_DATA[country];
  const vehicle = getSampleVehicleData(country, showLocalCurrency);
  
  const currencySymbol = showLocalCurrency ? data.currencySymbol : '$';
  const formatVal = (val) => `${currencySymbol}${val.toLocaleString()}`;

  return (
    <div className="mb-12" data-testid="legacy-vs-wisedrive">
      <div className="text-center mb-8">
        <Badge className="bg-red-100 text-red-600 border-red-200 mb-4">The Underwriting Challenge</Badge>
        <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
          Static Depreciation Models vs. Actual Asset Condition
        </h3>
        <p className="text-slate-600 max-w-3xl mx-auto">
          Traditional loan-to-value calculations rely on book depreciation—a methodology that fails to capture 
          mechanical wear, body damage, or hidden accident history. This creates systematic collateral overvaluation 
          and exposes your portfolio to elevated NPA risk.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Legacy Model */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-red-50 to-white border border-red-200 shadow-lg">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
              <XCircle size={24} className="text-red-500" />
            </div>
            <div>
              <h4 className="text-lg font-bold text-slate-900">Traditional Book Value Model</h4>
              <p className="text-sm text-red-600">Static Depreciation = Collateral Blind Spot</p>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Standard Input Parameters</div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">OEM:</span><span className="text-slate-700">{vehicle.make}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Model:</span><span className="text-slate-700">{vehicle.model}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">MY:</span><span className="text-slate-700">{vehicle.year}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Odometer:</span><span className="text-slate-700">{vehicle.odometer.toLocaleString()} km</span></div>
              </div>
            </div>

            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 text-center">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Calculated Collateral Value</div>
              <div className="text-3xl font-bold text-red-500">{formatVal(vehicle.displayBookValue)}</div>
              <div className="text-sm text-slate-500 mt-1">Avg. Loan Amount: {formatVal(vehicle.displayLoanAmount)}</div>
            </div>
          </div>

          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <AlertOctagon size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
              <span className="text-slate-600">No visibility into powertrain condition or pending failures</span>
            </div>
            <div className="flex items-start gap-3">
              <AlertOctagon size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
              <span className="text-slate-600">Body damage and structural repairs unaccounted</span>
            </div>
            <div className="flex items-start gap-3">
              <AlertOctagon size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
              <span className="text-slate-600">Prior accident history remains undisclosed</span>
            </div>
            <div className="flex items-start gap-3">
              <AlertOctagon size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
              <span className="text-slate-600">Third-party inspection reports susceptible to manipulation</span>
            </div>
          </div>
        </div>

        {/* Wisedrive Model */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 shadow-lg">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 size={24} className="text-emerald-600" />
            </div>
            <div>
              <h4 className="text-lg font-bold text-slate-900">Wisedrive AI-Enhanced Valuation</h4>
              <p className="text-sm text-emerald-600">Condition-Based Assessment = True Collateral Value</p>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">AI-Verified Condition Data</div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Book Value:</span><span className="text-emerald-600">{formatVal(vehicle.displayBookValue)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Engine Score:</span><span className="text-emerald-600">72/100 (B)</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Body Rating:</span><span className="text-emerald-600">Fair</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Accident:</span><span className="text-emerald-600">None</span></div>
              </div>
            </div>

            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Condition-Based Adjustments</div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">{vehicle.engineIssue}</span>
                  <span className="text-red-500">-{formatVal(vehicle.displayEngineDeduction)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">{vehicle.bodyIssue}</span>
                  <span className="text-red-500">-{formatVal(vehicle.displayBodyDeduction)}</span>
                </div>
              </div>
            </div>

            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 text-center">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Verified Collateral Value</div>
              <div className="text-3xl font-bold text-emerald-600">{formatVal(vehicle.displayTrueValue)}</div>
              <div className="text-sm text-slate-500 mt-1">condition-adjusted</div>
            </div>
          </div>

          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <span className="text-slate-600">Acoustic AI analysis of powertrain health indicators</span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <span className="text-slate-600">Computer vision quantifies all exterior damage with repair costs</span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <span className="text-slate-600">Forensic accident detection via structural anomaly analysis</span>
            </div>
            <div className="flex items-start gap-3">
              <Lock size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <span className="text-slate-600"><strong>Immutable AI audit trail</strong> - eliminates third-party manipulation risk</span>
            </div>
          </div>
        </div>
      </div>

      {/* Valuation Gap */}
      <div className="mt-8 p-6 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 shadow-lg">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center">
              <Scale size={32} className="text-amber-600" />
            </div>
            <div>
              <div className="text-sm text-amber-700 font-semibold uppercase tracking-wider">Collateral Overvaluation Exposure</div>
              <div className="text-2xl font-bold text-slate-900">{formatVal(vehicle.displayOvervaluation)} Gap Identified</div>
            </div>
          </div>
          <div className="text-center md:text-right">
            <div className="text-sm text-slate-600">Without condition-based assessment, this asset is</div>
            <div className="text-lg font-bold text-red-500">{vehicle.overvaluationPercent}% over-collateralized</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DualChartComponent = ({ legacyExpected, legacyActual, wisedrive, currencySymbol = '$' }) => {
  const allValues = [...legacyExpected, ...legacyActual, ...wisedrive].map(d => d.value);
  const maxValue = Math.max(...allValues);
  const minValue = Math.min(...allValues) * 0.85;
  const range = maxValue - minValue;
  
  const getY = (value) => {
    return 85 - ((value - minValue) / (range || 1)) * 70;
  };

  const createPath = (data) => {
    return data.map((point, i) => {
      const x = 8 + (i / (data.length - 1)) * 84;
      const y = getY(point.value);
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');
  };

  const legacyExpectedEnd = legacyExpected[legacyExpected.length - 1].value;
  const legacyActualEnd = legacyActual[legacyActual.length - 1].value;
  const wisedriveEnd = wisedrive[wisedrive.length - 1].value;
  
  const formatVal = (val) => `${currencySymbol}${val.toLocaleString()}`;

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Left Chart: Legacy Model - The Problem */}
      <div className="p-6 rounded-2xl bg-gradient-to-br from-red-50 to-white border border-red-200 shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
            <TrendingDown size={20} className="text-red-500" />
          </div>
          <div>
            <h4 className="font-bold text-slate-900">Without Wisedrive</h4>
            <p className="text-xs text-red-600">Hidden issues cause value shock</p>
          </div>
        </div>

        <svg viewBox="0 0 100 100" className="w-full h-52" preserveAspectRatio="xMidYMid meet">
          {/* Grid */}
          <line x1="8" y1="20" x2="92" y2="20" stroke="rgba(0,0,0,0.05)" strokeWidth="0.3" />
          <line x1="8" y1="45" x2="92" y2="45" stroke="rgba(0,0,0,0.05)" strokeWidth="0.3" />
          <line x1="8" y1="70" x2="92" y2="70" stroke="rgba(0,0,0,0.05)" strokeWidth="0.3" />
          <line x1="8" y1="15" x2="8" y2="90" stroke="rgba(0,0,0,0.15)" strokeWidth="0.3" />
          <line x1="8" y1="90" x2="92" y2="90" stroke="rgba(0,0,0,0.15)" strokeWidth="0.3" />

          {/* Shock zone fill */}
          <path 
            d={`${createPath(legacyExpected)} L 92 ${getY(legacyActualEnd)} ${createPath([...legacyActual].reverse()).replace('M', 'L')} Z`}
            fill="url(#redGradientLight)"
            opacity="0.4"
          />

          {/* Expected line (dashed) */}
          <path d={createPath(legacyExpected)} fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="4,3" />
          
          {/* Actual line (solid red) */}
          <path d={createPath(legacyActual)} fill="none" stroke="#ef4444" strokeWidth="2.5" />

          {/* Data points */}
          {legacyActual.map((point, i) => (
            <circle key={i} cx={8 + (i / (legacyActual.length - 1)) * 84} cy={getY(point.value)} r="2" fill="#ef4444" />
          ))}

          <defs>
            <linearGradient id="redGradientLight" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#ef4444" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#ef4444" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>

        {/* X-axis labels */}
        <div className="flex justify-between text-xs text-slate-500 px-2 mb-4">
          {legacyExpected.map((point, i) => <span key={i}>{point.label}</span>)}
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-4 pt-3 border-t border-red-200">
          <div className="flex items-center gap-2">
            <div className="w-6 h-0.5 bg-slate-400" style={{backgroundImage: 'repeating-linear-gradient(90deg, #94a3b8 0, #94a3b8 4px, transparent 4px, transparent 7px)'}}></div>
            <span className="text-xs text-slate-600">Expected: {formatVal(legacyExpectedEnd)}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-0.5 bg-red-500"></div>
            <span className="text-xs text-red-600">Actual: {formatVal(legacyActualEnd)}</span>
          </div>
        </div>

        {/* Shock metric */}
        <div className="mt-4 p-3 bg-red-50 rounded-xl border border-red-200 text-center">
          <div className="text-xs text-red-600 mb-1">Value Shock at NPA</div>
          <div className="text-2xl font-bold text-red-500">-{formatVal(legacyExpectedEnd - legacyActualEnd)}</div>
          <div className="text-xs text-red-600">{Math.round(((legacyExpectedEnd - legacyActualEnd) / legacyExpectedEnd) * 100)}% unexpected loss</div>
        </div>
      </div>

      {/* Right Chart: Wisedrive Model - The Solution */}
      <div className="p-6 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <ShieldCheck size={20} className="text-emerald-600" />
          </div>
          <div>
            <h4 className="font-bold text-slate-900">With Wisedrive Partnership</h4>
            <p className="text-xs text-emerald-600">Protected value through tenure</p>
          </div>
        </div>

        <svg viewBox="0 0 100 100" className="w-full h-52" preserveAspectRatio="xMidYMid meet">
          {/* Grid */}
          <line x1="8" y1="20" x2="92" y2="20" stroke="rgba(0,0,0,0.05)" strokeWidth="0.3" />
          <line x1="8" y1="45" x2="92" y2="45" stroke="rgba(0,0,0,0.05)" strokeWidth="0.3" />
          <line x1="8" y1="70" x2="92" y2="70" stroke="rgba(0,0,0,0.05)" strokeWidth="0.3" />
          <line x1="8" y1="15" x2="8" y2="90" stroke="rgba(0,0,0,0.15)" strokeWidth="0.3" />
          <line x1="8" y1="90" x2="92" y2="90" stroke="rgba(0,0,0,0.15)" strokeWidth="0.3" />

          {/* Protection zone fill */}
          <path 
            d={`${createPath(wisedrive)} L 92 90 L 8 90 Z`}
            fill="url(#greenGradientLight)"
            opacity="0.3"
          />

          {/* Wisedrive line (solid green) */}
          <path d={createPath(wisedrive)} fill="none" stroke="#10b981" strokeWidth="3" />

          {/* Comparison: where legacy would be (faded red) */}
          <path d={createPath(legacyActual)} fill="none" stroke="#ef4444" strokeWidth="1" strokeDasharray="2,2" opacity="0.4" />

          {/* Data points */}
          {wisedrive.map((point, i) => (
            <circle key={i} cx={8 + (i / (wisedrive.length - 1)) * 84} cy={getY(point.value)} r="2.5" fill="#10b981" />
          ))}

          <defs>
            <linearGradient id="greenGradientLight" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>

        {/* X-axis labels */}
        <div className="flex justify-between text-xs text-slate-500 px-2 mb-4">
          {wisedrive.map((point, i) => <span key={i}>{point.label}</span>)}
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-4 pt-3 border-t border-emerald-200">
          <div className="flex items-center gap-2">
            <div className="w-6 h-1 bg-emerald-500 rounded"></div>
            <span className="text-xs text-emerald-600">Protected: {formatVal(wisedriveEnd)}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-0.5 bg-red-400 opacity-40" style={{backgroundImage: 'repeating-linear-gradient(90deg, #f87171 0, #f87171 2px, transparent 2px, transparent 4px)'}}></div>
            <span className="text-xs text-slate-500">vs Legacy: {formatVal(legacyActualEnd)}</span>
          </div>
        </div>

        {/* Protection metric */}
        <div className="mt-4 p-3 bg-emerald-50 rounded-xl border border-emerald-200 text-center">
          <div className="text-xs text-emerald-700 mb-1">Value Protected vs Legacy</div>
          <div className="text-2xl font-bold text-emerald-600">+{formatVal(wisedriveEnd - legacyActualEnd)}</div>
          <div className="text-xs text-emerald-700">{Math.round((wisedriveEnd / wisedrive[0].value) * 100)}% value retention</div>
        </div>
      </div>
    </div>
  );
};

const ExtendedWarrantyPartnership = ({ country, showLocalCurrency }) => {
  const data = COUNTRY_DATA[country];
  const warranty = data.warrantyPricing;
  const vehicle = data.popularVehicle;
  
  const currencySymbol = showLocalCurrency ? data.currencySymbol : '$';
  const rate = showLocalCurrency ? data.exchangeRate : 1;
  
  const formatVal = (val) => `${currencySymbol}${Math.round(val * rate).toLocaleString()}`;
  
  // Calculate warranty costs
  const annualCost = warranty.annualCostUSD;
  const tenure = warranty.typicalLoanTenure;
  const totalWarrantyCost = annualCost * tenure;
  const loanAmount = vehicle.avgLoanAmount;
  const disbursedToCustomer = loanAmount - totalWarrantyCost;
  
  return (
    <div className="mb-12" data-testid="extended-warranty-partnership">
      {/* Hero Banner - DARK with emerald accent */}
      <div className="p-8 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 shadow-2xl mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div>
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mb-3">Ultimate Value Proposition</Badge>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Wisedrive Extended Warranty + Car Loan Bundle
            </h3>
            <p className="text-lg text-slate-300 max-w-2xl">
              The complete asset intelligence solution: Mandate Wisedrive Extended Warranty with every car loan 
              to get <strong className="text-emerald-400">free engine repairs</strong>, <strong className="text-emerald-400">maintained asset condition</strong>, 
              and <strong className="text-emerald-400">continuous health data</strong> flowing back to your underwriting models.
            </p>
          </div>
          <div className="flex-shrink-0">
            <div className="w-32 h-32 rounded-3xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-2xl shadow-emerald-500/20">
              <ShieldCheck size={64} className="text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Warranty Payment Model - Light section */}
      <div className="mb-8 p-6 rounded-2xl bg-white border border-slate-200 shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
            <DollarSign size={20} className="text-purple-600" />
          </div>
          <div>
            <h4 className="font-bold text-slate-900">How Warranty Payment Works</h4>
            <p className="text-sm text-slate-600">Customer-funded warranty, seamlessly integrated into loan disbursement</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Option A: Direct Deduction */}
          <div className="p-5 bg-slate-50 rounded-xl border border-purple-200">
            <div className="flex items-center gap-2 mb-3">
              <Badge className="bg-purple-100 text-purple-700 border-purple-200">Recommended</Badge>
              <span className="text-slate-900 font-semibold">Loan-Integrated Model</span>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center py-2 border-b border-slate-200">
                <span className="text-slate-600">Approved Loan Amount</span>
                <span className="text-slate-900 font-medium">{formatVal(loanAmount)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-200">
                <span className="text-slate-600">Warranty ({tenure} yrs × {formatVal(annualCost)}/yr)</span>
                <span className="text-red-500 font-medium">-{formatVal(totalWarrantyCost)}</span>
              </div>
              <div className="flex justify-between items-center py-2 bg-emerald-50 -mx-3 px-3 rounded">
                <span className="text-emerald-700 font-medium">Disbursed to Customer</span>
                <span className="text-emerald-700 font-bold">{formatVal(disbursedToCustomer)}</span>
              </div>
              <div className="flex justify-between items-center pt-2">
                <span className="text-purple-700 font-medium">Paid to Wisedrive</span>
                <span className="text-purple-700 font-bold">{formatVal(totalWarrantyCost)}</span>
              </div>
            </div>
          </div>

          {/* Option B: Customer Pays Separately */}
          <div className="p-5 bg-slate-50 rounded-xl border border-slate-200">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-slate-900 font-semibold">Customer Direct Payment</span>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center py-2 border-b border-slate-200">
                <span className="text-slate-600">Approved Loan Amount</span>
                <span className="text-slate-900 font-medium">{formatVal(loanAmount)}</span>
              </div>
              <div className="flex justify-between items-center py-2 bg-emerald-50 -mx-3 px-3 rounded">
                <span className="text-emerald-700 font-medium">Disbursed to Customer</span>
                <span className="text-emerald-700 font-bold">{formatVal(loanAmount)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-200">
                <span className="text-slate-600">Customer pays warranty</span>
                <span className="text-amber-600 font-medium">{formatVal(totalWarrantyCost)}</span>
              </div>
              <div className="flex justify-between items-center pt-2">
                <span className="text-slate-600">Total customer outlay</span>
                <span className="text-slate-900 font-bold">{formatVal(loanAmount + totalWarrantyCost)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
          <div className="flex items-start gap-3">
            <Info size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <span className="text-amber-700 font-medium">Why Loan-Integrated is Preferred: </span>
              <span className="text-slate-600 text-sm">
                Bank ensures 100% warranty compliance. No risk of customer opting out. 
                Warranty premium is built into LTV calculations from day one.
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* The Closed Loop System */}
      <div className="text-center mb-8">
        <h4 className="text-xl font-bold text-slate-900 mb-2">The Closed-Loop Asset Intelligence System</h4>
        <p className="text-slate-600">A virtuous cycle that protects your portfolio and improves over time</p>
      </div>

      {/* Circular Flow Diagram - DARK clean */}
      <div className="relative p-8 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 shadow-2xl mb-8">
        <div className="grid md:grid-cols-4 gap-4">
          {/* Step 1 */}
          <div className="text-center p-4">
            <div className="w-16 h-16 rounded-2xl bg-[#003399] flex items-center justify-center mx-auto mb-3 shadow-lg shadow-blue-500/20">
              <span className="text-2xl font-bold text-white">1</span>
            </div>
            <h5 className="font-semibold text-white mb-1">Bank Mandates Warranty</h5>
            <p className="text-xs text-slate-400">Every car loan includes Wisedrive Extended Warranty as a covenant</p>
          </div>

          {/* Step 2 */}
          <div className="text-center p-4">
            <div className="w-16 h-16 rounded-2xl bg-[#003399] flex items-center justify-center mx-auto mb-3 shadow-lg shadow-blue-500/20">
              <span className="text-2xl font-bold text-white">2</span>
            </div>
            <h5 className="font-semibold text-white mb-1">Free Engine Repairs</h5>
            <p className="text-xs text-slate-400">Wisedrive covers all engine repairs throughout loan tenure at zero cost</p>
          </div>

          {/* Step 3 */}
          <div className="text-center p-4">
            <div className="w-16 h-16 rounded-2xl bg-[#003399] flex items-center justify-center mx-auto mb-3 shadow-lg shadow-blue-500/20">
              <span className="text-2xl font-bold text-white">3</span>
            </div>
            <h5 className="font-semibold text-white mb-1">Asset Stays Healthy</h5>
            <p className="text-xs text-slate-400">Regular maintenance + repairs keep collateral in optimal condition</p>
          </div>

          {/* Step 4 */}
          <div className="text-center p-4">
            <div className="w-16 h-16 rounded-2xl bg-[#003399] flex items-center justify-center mx-auto mb-3 shadow-lg shadow-blue-500/20">
              <span className="text-2xl font-bold text-white">4</span>
            </div>
            <h5 className="font-semibold text-white mb-1">Data Flows to Bank</h5>
            <p className="text-xs text-slate-400">Health records & repair history feed back to improve underwriting</p>
          </div>
        </div>

        {/* Circular arrow indicator */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
          <div className="flex items-center gap-2 text-blue-400">
            <div className="w-12 h-0.5 bg-blue-500/50"></div>
            <span className="text-xs font-semibold">CONTINUOUS CYCLE</span>
            <div className="w-12 h-0.5 bg-blue-500/50"></div>
          </div>
        </div>
      </div>

      {/* Benefits Grid - Light theme */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* For the Bank */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-white border border-blue-200 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#003399] flex items-center justify-center">
              <Building2 size={20} className="text-white" />
            </div>
            <h4 className="font-bold text-slate-900">Benefits for Your Bank</h4>
          </div>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-slate-900 font-medium">Protected Collateral Value</span>
                <p className="text-sm text-slate-600">Engine repairs covered = no value erosion from mechanical failures</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-slate-900 font-medium">Real-Time Health Monitoring</span>
                <p className="text-sm text-slate-600">Know your portfolio's actual condition, not just book values</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-slate-900 font-medium">Underwriting Intelligence</span>
                <p className="text-sm text-slate-600">Historical repair data improves future loan decisions</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-slate-900 font-medium">Higher Recovery at NPA</span>
                <p className="text-sm text-slate-600">Well-maintained assets fetch better auction prices</p>
              </div>
            </li>
          </ul>
        </div>

        {/* For the Borrower */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center">
              <Users size={20} className="text-white" />
            </div>
            <h4 className="font-bold text-slate-900">Benefits for Borrowers</h4>
          </div>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-slate-900 font-medium">Zero Repair Costs</span>
                <p className="text-sm text-slate-600">All engine repairs covered during loan period</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-slate-900 font-medium">Protected Resale Value</span>
                <p className="text-sm text-slate-600">Car stays in good condition, worth more at loan end</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-slate-900 font-medium">Peace of Mind</span>
                <p className="text-sm text-slate-600">No unexpected repair bills that could affect EMI payments</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-slate-900 font-medium">Better Loan Terms</span>
                <p className="text-sm text-slate-600">Protected collateral enables competitive interest rates</p>
              </div>
            </li>
          </ul>
        </div>
      </div>

      {/* Data Feedback Loop - Light */}
      <div className="p-6 rounded-2xl bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 shadow-lg mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-cyan-600 flex items-center justify-center">
            <Activity size={20} className="text-white" />
          </div>
          <h4 className="font-bold text-slate-900">Asset Health Records → Better Underwriting</h4>
        </div>
        <p className="text-slate-600 mb-4">
          Every warranty claim, repair, and health check creates data that flows back to your underwriting models. 
          Over time, this builds a proprietary dataset that gives you competitive advantage:
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 bg-white rounded-xl border border-slate-100 shadow-sm">
            <div className="text-slate-700 font-semibold text-sm mb-1">Repair Frequency Data</div>
            <p className="text-xs text-slate-600">Know which makes/models have higher repair rates—adjust LTV accordingly</p>
          </div>
          <div className="p-4 bg-white rounded-xl border border-slate-100 shadow-sm">
            <div className="text-slate-700 font-semibold text-sm mb-1">Maintenance Compliance</div>
            <p className="text-xs text-slate-600">Track which borrowers maintain their vehicles—correlate with default risk</p>
          </div>
          <div className="p-4 bg-white rounded-xl border border-slate-100 shadow-sm">
            <div className="text-slate-700 font-semibold text-sm mb-1">Predictive Signals</div>
            <p className="text-xs text-slate-600">Sudden spike in repairs could indicate financial stress—early NPA warning</p>
          </div>
        </div>
      </div>

      {/* ROI Summary - DARK clean */}
      <div className="p-6 rounded-2xl bg-gradient-to-r from-slate-900 to-slate-800 border border-slate-700 shadow-xl">
        <div className="grid md:grid-cols-4 gap-6 text-center">
          <div>
            <div className="text-4xl font-bold text-emerald-400">100%</div>
            <div className="text-sm text-slate-300">Engine Repairs Covered</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-emerald-400">67%</div>
            <div className="text-sm text-slate-300">Asset Value Retention</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-emerald-400">35%</div>
            <div className="text-sm text-slate-300">NPL Risk Reduction</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-white">∞</div>
            <div className="text-sm text-slate-300">Underwriting Data Loop</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SubPrimeLendingOpportunity = ({ country }) => {
  const data = COUNTRY_DATA[country];
  
  return (
    <div className="mb-12 p-8 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 shadow-2xl" data-testid="subprime-lending">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
          <Users size={24} className="text-white" />
        </div>
        <div>
          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mb-1">Growth Opportunity</Badge>
          <h3 className="text-xl font-bold text-white">Expand to Sub-Prime & New-to-Credit Segments</h3>
        </div>
      </div>

      <p className="text-slate-300 mb-6">
        With Wisedrive's asset protection, banks can confidently extend credit to previously underserved segments. 
        When collateral value is verified and protected, the <strong className="text-white">Loss Given Default (LGD) 
        drops significantly</strong>—enabling lending to customers with thinner credit files while maintaining portfolio health.
      </p>

      <div className="grid md:grid-cols-3 gap-6 mb-6">
        <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-600 hover:border-emerald-500/50 transition-colors">
          <div className="flex items-center gap-2 mb-2">
            <Target size={18} className="text-emerald-400" />
            <span className="font-semibold text-white">Expand Addressable Market</span>
          </div>
          <p className="text-sm text-slate-400">
            First-time buyers, gig workers, and self-employed customers typically rejected for auto loans 
            become viable with verified collateral backing.
          </p>
        </div>

        <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-600 hover:border-emerald-500/50 transition-colors">
          <div className="flex items-center gap-2 mb-2">
            <Shield size={18} className="text-emerald-400" />
            <span className="font-semibold text-white">Protected Downside</span>
          </div>
          <p className="text-sm text-slate-400">
            Even if a sub-prime borrower defaults, recovery is predictable. LGD drops from 
            ~{100 - data.market.avgRecoveryRate}% to ~{100 - Math.min(data.market.avgRecoveryRate + 15, 85)}% with condition-verified assets.
          </p>
        </div>

        <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-600 hover:border-emerald-500/50 transition-colors">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={18} className="text-emerald-400" />
            <span className="font-semibold text-white">Grow Loan Book Safely</span>
          </div>
          <p className="text-sm text-slate-400">
            Increase disbursements by 15-20% without proportional risk increase. 
            Risk-adjusted yields improve as higher rates offset managed default exposure.
          </p>
        </div>
      </div>

      <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h4 className="font-semibold text-white mb-1">The Math: Sub-Prime with Asset Protection</h4>
            <p className="text-sm text-slate-400">
              Traditional sub-prime: 8% default × 40% LGD = <span className="text-red-400 font-bold">3.2% portfolio loss</span><br />
              Wisedrive-backed sub-prime: 8% default × 25% LGD = <span className="text-emerald-400 font-bold">2.0% portfolio loss</span>
            </p>
          </div>
          <div className="text-center md:text-right">
            <div className="text-sm text-emerald-400">Net Improvement</div>
            <div className="text-3xl font-bold text-emerald-400">37.5%</div>
            <div className="text-xs text-slate-400">lower losses on sub-prime</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PortfolioRiskManagement = ({ country, showLocalCurrency }) => {
  const data = COUNTRY_DATA[country];
  const portfolioData = getPortfolioValueData(country, showLocalCurrency);
  const { legacyExpected, legacyActual, wisedrive, metrics } = portfolioData;
  
  const currencySymbol = showLocalCurrency ? data.currencySymbol : '$';
  const legacyExpectedEnd = legacyExpected[legacyExpected.length - 1].value;
  const legacyActualEnd = legacyActual[legacyActual.length - 1].value;
  const wisedriveEnd = wisedrive[wisedrive.length - 1].value;
  
  const formatVal = (val) => `${currencySymbol}${val.toLocaleString()}`;
  const formatMillions = (val) => {
    if (showLocalCurrency) {
      // For local currency, show in Millions/Crores as appropriate
      return `${currencySymbol}${(val / 1000000).toFixed(1)}M`;
    }
    return `$${(val / 1000000).toFixed(1)}M`;
  };

  return (
    <div className="mb-12" data-testid="portfolio-risk-management">
      <div className="text-center mb-8">
        <Badge className="bg-blue-100 text-blue-700 border-blue-200 mb-4">Portfolio Intelligence</Badge>
        <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
          Used Car Financing Portfolio Risk Management
        </h3>
        <p className="text-slate-600 max-w-3xl mx-auto">
          Compare collateral value trajectories over a 5-year loan tenure. Without condition-based assessment, 
          hidden issues surface causing unexpected erosion. With Wisedrive, values remain protected and predictable.
        </p>
      </div>

      {/* Dual Charts */}
      <DualChartComponent 
        legacyExpected={legacyExpected}
        legacyActual={legacyActual}
        wisedrive={wisedrive}
        currencySymbol={currencySymbol}
      />

      {/* Portfolio Impact Summary - DARK for IMPACT */}
      <div className="mt-8 p-8 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 shadow-2xl">
        <div className="text-center mb-4">
          <span className="text-emerald-400 text-sm font-bold uppercase tracking-wider">The Wisedrive Advantage</span>
        </div>
        <div className="grid md:grid-cols-4 gap-6 text-center">
          <div>
            <div className="text-3xl font-bold text-white">+{formatVal(wisedriveEnd - legacyActualEnd)}</div>
            <div className="text-xs text-slate-300">Additional Recovery Per Asset</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-emerald-400">~{metrics.nplRiskReduction}%</div>
            <div className="text-xs text-slate-300">NPL Risk Reduction</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-emerald-400">+{formatMillions((wisedriveEnd - legacyActualEnd) * 1000)}</div>
            <div className="text-xs text-slate-300">Per 1,000 Loan Portfolio</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-emerald-400">{Math.round((wisedriveEnd / wisedrive[0].value) * 100)}%</div>
            <div className="text-xs text-slate-300">5-Year Value Retention</div>
          </div>
        </div>
      </div>

      {/* Extended Warranty Partnership - THE KEY VALUE PROPOSITION */}
      <div className="mt-8">
        <ExtendedWarrantyPartnership country={country} showLocalCurrency={showLocalCurrency} />
      </div>

      {/* Sub-Prime Lending Section */}
      <div className="mt-0">
        <SubPrimeLendingOpportunity country={country} />
      </div>

      {/* How Wisedrive Protects Your Portfolio - DARK for EMPHASIS */}
      <div className="p-8 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 shadow-2xl">
        <h4 className="text-xl font-bold text-white mb-6 text-center">How Wisedrive Partnership Protects Your Portfolio</h4>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mx-auto mb-3 shadow-lg shadow-emerald-500/20">
              <span className="text-2xl font-bold text-white">1</span>
            </div>
            <h5 className="font-semibold text-white mb-2">Day Zero: Accurate Baseline</h5>
            <p className="text-sm text-slate-400">
              Start with verified collateral value, not inflated book value. No overexposure from day one.
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mx-auto mb-3 shadow-lg shadow-emerald-500/20">
              <span className="text-2xl font-bold text-white">2</span>
            </div>
            <h5 className="font-semibold text-white mb-2">Year 1-2: Issue Prevention</h5>
            <p className="text-sm text-slate-400">
              Identified issues addressed under warranty mandates. No mid-tenure value crashes.
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mx-auto mb-3 shadow-lg shadow-emerald-500/20">
              <span className="text-2xl font-bold text-white">3</span>
            </div>
            <h5 className="font-semibold text-white mb-2">Year 3-4: Predictable Curve</h5>
            <p className="text-sm text-slate-400">
              Depreciation follows expected trajectory. Portfolio models remain accurate for provisioning.
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mx-auto mb-3 shadow-lg shadow-emerald-500/20">
              <span className="text-2xl font-bold text-white">4</span>
            </div>
            <h5 className="font-semibold text-white mb-2">Year 5: Strong Recovery</h5>
            <p className="text-sm text-slate-400">
              At maturity or NPA, asset retains {Math.round((wisedriveEnd / wisedrive[0].value) * 100)}% value vs {Math.round((legacyActualEnd / legacyExpected[0].value) * 100)}% under legacy model.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const AntiFraudBenefits = ({ country }) => {
  const data = COUNTRY_DATA[country];
  
  return (
    <div className="mb-12" data-testid="anti-fraud-benefits">
      {/* Dark header banner */}
      <div className="p-6 rounded-t-3xl bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="text-center">
          <Badge className="bg-slate-700/50 text-slate-300 border-slate-600 mb-3">Fraud Mitigation</Badge>
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
            Eliminating Third-Party Inspection Risk
          </h3>
          <p className="text-slate-300 max-w-3xl mx-auto">
            Global auto lending fraud exceeded <span className="text-red-400 font-bold">$9.2 billion</span> in 2024—a 16.5% YoY increase. In {data.name}, 
            estimated exposure reaches <span className="text-red-400 font-bold">${data.market.fraudExposure}M</span> annually.
          </p>
        </div>
      </div>

      {/* Light content cards */}
      <div className="p-6 rounded-b-3xl bg-slate-50 border border-t-0 border-slate-200">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-lg hover:border-red-300 transition-colors">
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center mb-4">
              <AlertOctagon size={24} className="text-red-500" />
            </div>
            <h4 className="font-bold text-slate-900 mb-2">The Inspection Fraud Vector</h4>
            <p className="text-sm text-slate-600 mb-4">
              Third-party mechanics are routinely incentivized by sellers to suppress negative findings. 
              Odometer tampering, accident concealment, and inflated condition reports create systematic 
              collateral overvaluation across portfolios.
            </p>
            <div className="text-sm text-red-500 font-semibold">
              ${data.market.fraudExposure}M estimated annual exposure in {data.name}
            </div>
          </div>

          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-lg hover:border-slate-400 transition-colors">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
              <Lock size={24} className="text-slate-600" />
            </div>
            <h4 className="font-bold text-slate-900 mb-2">AI-Based Verification</h4>
            <p className="text-sm text-slate-600 mb-4">
              Wisedrive's acoustic and computer vision analysis operates independently of human bias. 
              Engine health is determined by sound frequency patterns; damage is quantified by pixel-level 
              image analysis. No intermediary can influence outcomes.
            </p>
            <div className="text-sm text-slate-700 font-semibold">
              100% algorithmic—zero human intervention in assessment
            </div>
          </div>

          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-lg hover:border-emerald-300 transition-colors">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
              <ShieldCheck size={24} className="text-emerald-600" />
            </div>
            <h4 className="font-bold text-slate-900 mb-2">Immutable Audit Trail</h4>
            <p className="text-sm text-slate-600 mb-4">
              Every analysis generates a unique identifier with timestamped raw media, processing logs, 
              and findings. This creates a complete evidentiary chain for regulatory audits, 
              dispute resolution, and portfolio due diligence.
            </p>
            <div className="text-sm text-emerald-600 font-semibold">
              Full compliance with central bank audit requirements
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ROIForBanks = ({ country }) => {
  const data = COUNTRY_DATA[country];
  
  return (
    <div className="mb-12 p-8 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 shadow-2xl" data-testid="roi-for-banks">
      <div className="text-center mb-8">
        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mb-4">Business Case</Badge>
        <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
          Quantified Portfolio Impact
        </h3>
        <p className="text-slate-400">The numbers that matter to your bottom line</p>
      </div>

      <div className="grid md:grid-cols-4 gap-6">
        {[
          { 
            value: `$${Math.round(data.market.avgLoanSize * 0.28 / 100) * 100}`, 
            label: "Avg. Recovery Uplift", 
            sublabel: "Per NPA with accurate condition data",
            icon: TrendingUp
          },
          { 
            value: `${Math.round(35 - (data.market.nplRate * 2))}%`, 
            label: "NPL Reduction Potential", 
            sublabel: "Via pre-disbursement condition screening",
            icon: Shield
          },
          { 
            value: "10x", 
            label: "Assessment Velocity", 
            sublabel: "AI report in 3 min vs 48hr physical inspection",
            icon: Zap
          },
          { 
            value: "Zero", 
            label: "Third-Party Risk", 
            sublabel: "Algorithmic assessment eliminates manipulation",
            icon: Lock
          }
        ].map((metric, i) => {
          const Icon = metric.icon;
          return (
            <div key={i} className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700 text-center hover:border-emerald-500/50 transition-colors">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/20">
                <Icon size={28} className="text-white" />
              </div>
              <div className="text-4xl font-bold text-white mb-2">{metric.value}</div>
              <div className="text-sm font-semibold text-slate-300 mb-2">{metric.label}</div>
              <div className="text-xs text-slate-500">{metric.sublabel}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============ SECTION COMPONENTS ============

const BanksSection = ({ country, showLocalCurrency }) => {
  return (
    <div data-testid="banks-section">
      <MarketIntelligencePanel country={country} showLocalCurrency={showLocalCurrency} />
      <LegacyVsWisedriveComparison country={country} showLocalCurrency={showLocalCurrency} />
      <PortfolioRiskManagement country={country} showLocalCurrency={showLocalCurrency} />
      <GenZBuyingTrends industry="banks" />
      <AntiFraudBenefits country={country} />
      <ROIForBanks country={country} />

      {/* CTA Section - Dark for impact */}
      <div className="p-8 rounded-3xl bg-gradient-to-r from-[#003399] via-[#0044CC] to-[#003399] text-center shadow-2xl">
        <h4 className="text-2xl font-bold text-white mb-3">Ready to Protect Your Portfolio?</h4>
        <p className="text-blue-100 mb-6 max-w-xl mx-auto">Get a personalized ROI analysis for your auto loan portfolio</p>
        <Button 
          size="lg"
          className="bg-white hover:bg-slate-100 text-[#003399] px-10 h-14 text-lg gap-2 rounded-full shadow-lg font-bold"
          data-testid="banks-demo-btn"
        >
          <Play size={20} />
          Request Portfolio Assessment Demo
        </Button>
      </div>
    </div>
  );
};

// ============ INSURANCE SECTION COMPONENTS ============

const InsuranceMarketIntelligence = ({ country, showLocalCurrency }) => {
  const data = COUNTRY_DATA[country];
  const ins = data.insurance;
  const extWarranty = data.extendedWarrantyMarket;
  
  // Extended Warranty Opportunity = New policies per year * 20% of avg premium
  const warrantyOpportunity = (ins.newPoliciesPerYear * ins.avgPremium * 0.20) / 1000; // In billions
  
  const formatBillions = (valueB) => {
    if (showLocalCurrency) {
      const localVal = valueB * data.exchangeRate;
      return `${data.currencySymbol}${localVal.toFixed(1)}B`;
    }
    return `$${valueB.toFixed(2)}B`;
  };
  
  const formatMillions = (valueM) => {
    if (showLocalCurrency) {
      const localVal = Math.round(valueM * data.exchangeRate);
      return `${data.currencySymbol}${localVal.toLocaleString()}M`;
    }
    return `$${valueM}M`;
  };
  
  return (
    <div className="mb-12 p-8 rounded-3xl bg-white border border-slate-200 shadow-xl" data-testid="insurance-market-intelligence">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
        {/* Left: Market Overview */}
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-4xl">{data.flag}</span>
            <div>
              <Badge className="bg-purple-100 text-purple-700 border-purple-200 mb-1">Insurance Market Intelligence</Badge>
              <h3 className="text-2xl font-bold text-slate-900">{data.name} Motor Insurance Market</h3>
            </div>
          </div>
          
          <p className="text-lg text-purple-600 font-semibold mb-4">{data.insuranceInsights.headline}</p>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Motor Insurance Market</div>
              <div className="text-2xl font-bold text-slate-900">{formatBillions(ins.motorInsuranceMarket)}</div>
              <div className="text-xs text-emerald-600">→ {formatBillions(ins.projectedMarket2030)} by 2030</div>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Claims/Loss Ratio</div>
              <div className="text-2xl font-bold text-amber-500">{ins.claimsRatio}%</div>
              <div className="text-xs text-slate-500">Combined Ratio: {ins.combinedRatioPSU}%</div>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Fraud Rate</div>
              <div className="text-2xl font-bold text-red-500">{ins.fraudRate}%</div>
              <div className="text-xs text-slate-500">Exposure: {formatMillions(ins.fraudExposure)}</div>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Extended Warranty Opportunity</div>
              <div className="text-2xl font-bold text-emerald-600">{formatBillions(warrantyOpportunity)}</div>
              <div className="text-xs text-slate-500">{ins.newPoliciesPerYear}M policies × 20% premium</div>
            </div>
          </div>
          
          {/* Insurance Penetration */}
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-slate-600">Insurance Penetration</span>
              <span className="text-sm text-slate-900 font-semibold">{Math.round((ins.insuredVehicles / ins.registeredVehicles) * 100)}%</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div 
                className="bg-purple-500 h-2 rounded-full" 
                style={{ width: `${(ins.insuredVehicles / ins.registeredVehicles) * 100}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>{ins.insuredVehicles}M insured</span>
              <span>{ins.registeredVehicles}M registered vehicles</span>
            </div>
          </div>
        </div>

        {/* Right: Key Insights */}
        <div className="lg:w-[400px] space-y-4">
          <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
            <div className="text-xs text-emerald-700 font-bold uppercase tracking-wider mb-2">Key Market Statistics</div>
            <ul className="space-y-2">
              {data.insuranceInsights.keyStats.map((stat, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                  {stat}
                </li>
              ))}
            </ul>
          </div>
          
          <div className="p-4 bg-red-50 rounded-xl border border-red-200">
            <div className="text-xs text-red-700 font-bold uppercase tracking-wider mb-2">Pain Points & Opportunities</div>
            <ul className="space-y-2">
              {data.insuranceInsights.painPoints.map((pain, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <AlertTriangle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
                  {pain}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Gen-Z Buying Behavior Component - DARK with clean single accent
const GenZBuyingTrends = ({ industry }) => {
  const isInsurance = industry === 'insurance';
  
  return (
    <div className="mb-12 p-8 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 shadow-2xl" data-testid="genz-trends">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center shadow-lg">
              <Users size={28} className="text-white" />
            </div>
            <div>
              <Badge className="bg-slate-700/50 text-slate-300 border-slate-600 mb-1">The Future Customer</Badge>
              <h3 className="text-2xl font-bold text-white">Gen-Z: The Bundled Experience Generation</h3>
            </div>
          </div>
          
          <p className="text-lg text-slate-300 mb-6">
            {isInsurance 
              ? "New-age car buyers demand peace of mind through one-stop solutions. Gen-Z doesn't want separate insurance and warranty—they want complete protection in a single purchase."
              : "Young borrowers expect financial products that just work. They don't want to worry about separate insurance, warranty, and maintenance—they want a car loan that covers everything."
            }
          </p>

          {/* Gen-Z Stats - clean single color */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-slate-800/80 rounded-xl border border-slate-700 text-center">
              <div className="text-4xl font-bold text-white">73%</div>
              <div className="text-xs text-slate-400">Prefer bundled products</div>
            </div>
            <div className="p-4 bg-slate-800/80 rounded-xl border border-slate-700 text-center">
              <div className="text-4xl font-bold text-white">2.4x</div>
              <div className="text-xs text-slate-400">More likely to buy add-ons</div>
            </div>
            <div className="p-4 bg-slate-800/80 rounded-xl border border-slate-700 text-center">
              <div className="text-4xl font-bold text-white">68%</div>
              <div className="text-xs text-slate-400">Want digital-first experience</div>
            </div>
            <div className="p-4 bg-slate-800/80 rounded-xl border border-slate-700 text-center">
              <div className="text-4xl font-bold text-white">89%</div>
              <div className="text-xs text-slate-400">Value "peace of mind"</div>
            </div>
          </div>
        </div>

        {/* Right: Key Insights */}
        <div className="lg:w-[380px] space-y-4">
          <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
            <div className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-3">Why Bundling Wins with Gen-Z</div>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-sm text-slate-300">
                <CheckCircle2 size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">One-stop shop mentality:</strong> "Just give me everything I need"</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-slate-300">
                <CheckCircle2 size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">Subscription mindset:</strong> Used to Netflix, Spotify bundles</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-slate-300">
                <CheckCircle2 size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">Anxiety about unknowns:</strong> Prefer comprehensive coverage</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-slate-300">
                <CheckCircle2 size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">Trust through simplicity:</strong> Less paperwork = more trust</span>
              </li>
            </ul>
          </div>

          <div className="p-4 bg-slate-700/30 rounded-xl border border-slate-600">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp size={18} className="text-emerald-400" />
              <span className="text-sm font-bold text-slate-300">Market Shift</span>
            </div>
            <p className="text-xs text-slate-400">
              By 2030, Gen-Z will represent <strong className="text-white">45% of car buyers</strong> in Asia. 
              {isInsurance 
                ? " Insurance companies offering bundled warranty will capture this growing segment."
                : " Banks offering comprehensive loan packages will win their loyalty."
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// AI Technology Showcase Component for Insurance - DARK for IMPACT
const AITechnologyShowcase = ({ country, showLocalCurrency }) => {
  return (
    <div className="mb-12" data-testid="ai-technology-showcase">
      {/* Dark header */}
      <div className="p-8 rounded-t-3xl bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="text-center">
          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mb-4">AI-Powered Technology</Badge>
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
            What Wisedrive AI Sees That Humans Miss
          </h3>
          <p className="text-slate-300 max-w-3xl mx-auto">
            Our computer vision and acoustic AI detect damage invisible to manual inspection. 
            Every assessment creates an immutable record for claims and fraud prevention.
          </p>
        </div>
      </div>

      {/* Light content */}
      <div className="p-6 bg-slate-50 border border-t-0 border-slate-200">
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Vision AI - Exterior */}
          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-lg">
            <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mb-4">
              <Eye size={28} className="text-emerald-600" />
            </div>
            <h4 className="text-lg font-bold text-slate-900 mb-2">Vision AI: Exterior Analysis</h4>
            <div className="space-y-3 mb-4">
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-slate-600">Dent Detection</span>
                  <span className="text-xs text-emerald-600 font-bold">98.2% accuracy</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1.5">
                  <div className="bg-emerald-500 h-1.5 rounded-full" style={{width: '98%'}}></div>
                </div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-slate-600">Scratch Analysis</span>
                  <span className="text-xs text-emerald-600 font-bold">96.8% accuracy</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1.5">
                  <div className="bg-emerald-500 h-1.5 rounded-full" style={{width: '97%'}}></div>
                </div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-slate-600">Paint Damage</span>
                  <span className="text-xs text-emerald-600 font-bold">94.5% accuracy</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1.5">
                  <div className="bg-emerald-500 h-1.5 rounded-full" style={{width: '94%'}}></div>
                </div>
              </div>
            </div>
            <div className="text-xs text-slate-600">
              AI identifies damage size, location, and repair cost estimate automatically
            </div>
          </div>

          {/* Vision AI - Accident Detection */}
          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-lg">
            <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center mb-4">
              <AlertTriangle size={28} className="text-red-500" />
            </div>
            <h4 className="text-lg font-bold text-slate-900 mb-2">Vision AI: Accident Detection</h4>
            <div className="space-y-3 mb-4">
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-slate-600">Impact Point Detection</span>
                  <span className="text-xs text-emerald-600 font-bold">97.1% accuracy</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1.5">
                  <div className="bg-red-500 h-1.5 rounded-full" style={{width: '97%'}}></div>
                </div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-slate-600">Structural Damage</span>
                  <span className="text-xs text-emerald-600 font-bold">95.3% accuracy</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1.5">
                  <div className="bg-red-500 h-1.5 rounded-full" style={{width: '95%'}}></div>
                </div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-slate-600">Severity Assessment</span>
                  <span className="text-xs text-emerald-600 font-bold">93.7% accuracy</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1.5">
                  <div className="bg-red-500 h-1.5 rounded-full" style={{width: '94%'}}></div>
                </div>
              </div>
            </div>
            <div className="text-xs text-slate-600">
              Classifies accidents as Minor, Moderate, or Severe with repair timeline
            </div>
          </div>

          {/* Sound AI - Engine */}
          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-lg">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
              <AudioWaveform size={28} className="text-slate-600" />
            </div>
            <h4 className="text-lg font-bold text-slate-900 mb-2">Sound AI: Engine Analysis</h4>
            <div className="space-y-3 mb-4">
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-slate-600">Knock Detection</span>
                  <span className="text-xs text-emerald-600 font-bold">94.8% accuracy</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1.5">
                  <div className="bg-slate-500 h-1.5 rounded-full" style={{width: '95%'}}></div>
                </div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-slate-600">Timing Chain Wear</span>
                  <span className="text-xs text-emerald-600 font-bold">92.4% accuracy</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1.5">
                  <div className="bg-slate-500 h-1.5 rounded-full" style={{width: '92%'}}></div>
                </div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-slate-600">Overall Health Score</span>
                  <span className="text-xs text-emerald-600 font-bold">96.1% accuracy</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1.5">
                  <div className="bg-slate-500 h-1.5 rounded-full" style={{width: '96%'}}></div>
                </div>
              </div>
            </div>
            <div className="text-xs text-slate-600">
              Analyzes engine sound via video upload to predict failures before they happen
            </div>
          </div>
        </div>
      </div>

      {/* Dark metrics banner */}
      <div className="p-6 rounded-b-3xl bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="grid md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-3xl font-bold text-white">30 sec</div>
            <div className="text-xs text-slate-400">Analysis Time</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-emerald-400">95%+</div>
            <div className="text-xs text-slate-400">Detection Accuracy</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-white">100%</div>
            <div className="text-xs text-slate-400">Audit Trail</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-white">Zero</div>
            <div className="text-xs text-slate-400">Human Bias</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InsuranceInspectionComparison = ({ country, showLocalCurrency }) => {
  const data = COUNTRY_DATA[country];
  const vehicle = data.popularVehicle;
  const ins = data.insurance;
  
  const currencySymbol = showLocalCurrency ? data.currencySymbol : '$';
  const rate = showLocalCurrency ? data.exchangeRate : 1;
  const formatVal = (val) => `${currencySymbol}${Math.round(val * rate).toLocaleString()}`;

  // Calculate IDV values
  const standardIDV = vehicle.bookValueUSD;
  const wisedriveIDV = vehicle.trueValueUSD + 500; // Higher due to verified condition
  const premiumDiff = Math.round((wisedriveIDV - standardIDV) * 0.04); // ~4% of IDV difference

  return (
    <div className="mb-12" data-testid="insurance-inspection-comparison">
      <div className="text-center mb-8">
        <Badge className="bg-purple-100 text-purple-700 border-purple-200 mb-4">The Inspection Challenge</Badge>
        <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
          Traditional Inspection vs. AI-Powered Assessment
        </h3>
        <p className="text-slate-600 max-w-3xl mx-auto">
          Standard motor insurance inspections focus on exterior and interior—completely ignoring engine condition. 
          This creates blind spots in IDV calculation and claim assessment.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Traditional Inspection */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-200 shadow-lg">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
              <Eye size={24} className="text-slate-500" />
            </div>
            <div>
              <h4 className="text-lg font-bold text-slate-900">Traditional Pre-Insurance Inspection</h4>
              <p className="text-sm text-slate-500">Manual, exterior-focused assessment</p>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-3">What Gets Inspected</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  <span className="text-sm text-slate-700">Exterior Body</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  <span className="text-sm text-slate-700">Paint Condition</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  <span className="text-sm text-slate-700">Interior State</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  <span className="text-sm text-slate-700">Odometer Reading</span>
                </div>
              </div>
            </div>

            <div className="p-4 bg-red-50 rounded-xl border border-red-200">
              <div className="text-xs text-red-600 uppercase tracking-wider mb-3">What Gets MISSED</div>
              <div className="grid grid-cols-1 gap-2">
                <div className="flex items-center gap-2">
                  <XCircle size={16} className="text-red-500" />
                  <span className="text-sm text-slate-700">Engine Health (wear & tear = not covered)</span>
                </div>
                <div className="flex items-center gap-2">
                  <XCircle size={16} className="text-red-500" />
                  <span className="text-sm text-slate-700">Hidden Mechanical Issues</span>
                </div>
                <div className="flex items-center gap-2">
                  <XCircle size={16} className="text-red-500" />
                  <span className="text-sm text-slate-700">Accident History Detection</span>
                </div>
                <div className="flex items-center gap-2">
                  <XCircle size={16} className="text-red-500" />
                  <span className="text-sm text-slate-700">Structural Damage Assessment</span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 text-center">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Standard IDV Calculation</div>
            <div className="text-3xl font-bold text-slate-500">{formatVal(standardIDV)}</div>
            <div className="text-sm text-slate-500 mt-1">Based on age + mileage only</div>
          </div>
        </div>

        {/* Wisedrive Inspection */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-purple-50 to-white border border-purple-200 shadow-lg">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
              <Sparkles size={24} className="text-purple-600" />
            </div>
            <div>
              <h4 className="text-lg font-bold text-slate-900">Wisedrive AI-Powered Inspection</h4>
              <p className="text-sm text-purple-600">Complete vehicle intelligence</p>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="text-xs text-purple-600 uppercase tracking-wider mb-3">Full Assessment Coverage</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  <span className="text-sm text-slate-700">AI Dent Detection</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  <span className="text-sm text-slate-700">Scratch Analysis</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  <span className="text-sm text-slate-700">Accident Detection</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  <span className="text-sm text-slate-700">Damage Severity Level</span>
                </div>
              </div>
            </div>

            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
              <div className="text-xs text-emerald-700 uppercase tracking-wider mb-3">PLUS: Engine Assessment</div>
              <div className="grid grid-cols-1 gap-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  <span className="text-sm text-slate-700">Engine Sound Analysis (via video upload)</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  <span className="text-sm text-slate-700">Mechanical Health Score</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  <span className="text-sm text-slate-700">Predictive Failure Risk</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  <span className="text-sm text-slate-700">Extended Warranty Eligibility</span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 bg-purple-50 rounded-xl border border-purple-100 text-center">
            <div className="text-xs text-purple-600 uppercase tracking-wider mb-2">Condition-Based IDV</div>
            <div className="text-3xl font-bold text-purple-600">{formatVal(wisedriveIDV)}</div>
            <div className="text-sm text-emerald-600 mt-1">+{formatVal(wisedriveIDV - standardIDV)} justified premium</div>
          </div>
        </div>
      </div>

      {/* IDV Impact Banner */}
      <div className="mt-8 p-6 rounded-2xl bg-gradient-to-r from-emerald-50 to-purple-50 border border-emerald-200 shadow-lg">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center">
              <TrendingUp size={32} className="text-emerald-600" />
            </div>
            <div>
              <div className="text-sm text-emerald-700 font-semibold uppercase tracking-wider">IDV Optimization Opportunity</div>
              <div className="text-2xl font-bold text-slate-900">Higher IDV = Higher Premium, Lower Claim Disputes</div>
            </div>
          </div>
          <div className="text-center md:text-right">
            <div className="text-sm text-slate-600">Per-vehicle premium increase potential</div>
            <div className="text-lg font-bold text-emerald-600">+{formatVal(premiumDiff)} annually</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InsuranceClaimsOptimization = ({ country, showLocalCurrency }) => {
  const data = COUNTRY_DATA[country];
  const ins = data.insurance;
  
  const currencySymbol = showLocalCurrency ? data.currencySymbol : '$';
  const rate = showLocalCurrency ? data.exchangeRate : 1;
  const formatVal = (val) => `${currencySymbol}${Math.round(val * rate).toLocaleString()}`;
  const formatMillions = (val) => `${currencySymbol}${Math.round(val * rate).toLocaleString()}M`;

  // Calculate potential savings
  const fraudReductionPotential = ins.fraudExposure * 0.4; // 40% fraud reduction with AI
  const claimsReductionPotential = ins.motorInsuranceMarket * 1000 * 0.05; // 5% claims reduction
  
  return (
    <div className="mb-12" data-testid="insurance-claims-optimization">
      <div className="text-center mb-8">
        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 mb-4">Claims Intelligence</Badge>
        <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
          AI-Powered Claims Assessment & Fraud Prevention
        </h3>
        <p className="text-slate-600 max-w-3xl mx-auto">
          Wisedrive Vision AI provides objective, quantified damage assessment—reducing claim disputes, 
          identifying fraud, and enabling faster settlement.
        </p>
      </div>

      {/* Claims Flow Comparison */}
      <div className="grid lg:grid-cols-2 gap-8 mb-8">
        {/* Without Wisedrive */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-200 shadow-lg">
          <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <XCircle size={20} className="text-red-500" />
            Without Wisedrive
          </h4>
          <div className="space-y-3">
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 flex items-center justify-between">
              <span className="text-slate-600">Claim Assessment</span>
              <span className="text-red-500 text-sm">Manual surveyor - 3-5 days</span>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 flex items-center justify-between">
              <span className="text-slate-600">Damage Quantification</span>
              <span className="text-red-500 text-sm">Subjective estimates</span>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 flex items-center justify-between">
              <span className="text-slate-600">Fraud Detection</span>
              <span className="text-red-500 text-sm">Manual review only</span>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 flex items-center justify-between">
              <span className="text-slate-600">Pre-existing Damage</span>
              <span className="text-red-500 text-sm">Disputes common</span>
            </div>
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="text-center">
                <div className="text-sm text-red-600">Annual Fraud Loss</div>
                <div className="text-2xl font-bold text-red-500">{formatMillions(ins.fraudExposure)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* With Wisedrive */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 shadow-lg">
          <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <CheckCircle2 size={20} className="text-emerald-600" />
            With Wisedrive Partnership
          </h4>
          <div className="space-y-3">
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 flex items-center justify-between">
              <span className="text-slate-600">Claim Assessment</span>
              <span className="text-emerald-600 text-sm">AI analysis - instant</span>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 flex items-center justify-between">
              <span className="text-slate-600">Damage Quantification</span>
              <span className="text-emerald-600 text-sm">Pixel-level with cost estimate</span>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 flex items-center justify-between">
              <span className="text-slate-600">Fraud Detection</span>
              <span className="text-emerald-600 text-sm">AI anomaly detection</span>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 flex items-center justify-between">
              <span className="text-slate-600">Pre-existing Damage</span>
              <span className="text-emerald-600 text-sm">Photo baseline proof</span>
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="text-center">
                <div className="text-sm text-emerald-700">Potential Fraud Savings</div>
                <div className="text-2xl font-bold text-emerald-600">{formatMillions(fraudReductionPotential)}</div>
                <div className="text-xs text-slate-600">40% fraud reduction with AI</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-lg text-center">
          <div className="text-3xl font-bold text-emerald-600">40%</div>
          <div className="text-sm text-slate-600">Fraud Detection Improvement</div>
        </div>
        <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-lg text-center">
          <div className="text-3xl font-bold text-purple-600">85%</div>
          <div className="text-sm text-slate-600">Faster Claims Processing</div>
        </div>
        <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-lg text-center">
          <div className="text-3xl font-bold text-blue-600">60%</div>
          <div className="text-sm text-slate-600">Reduction in Disputes</div>
        </div>
        <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-lg text-center">
          <div className="text-3xl font-bold text-amber-600">95%</div>
          <div className="text-sm text-slate-600">Damage Detection Accuracy</div>
        </div>
      </div>
    </div>
  );
};

const ExtendedWarrantyPartnershipInsurance = ({ country, showLocalCurrency }) => {
  const data = COUNTRY_DATA[country];
  const extWarranty = data.extendedWarrantyMarket;
  const ins = data.insurance;
  const warranty = data.warrantyPricing;
  
  const currencySymbol = showLocalCurrency ? data.currencySymbol : '$';
  const rate = showLocalCurrency ? data.exchangeRate : 1;
  const formatVal = (val) => `${currencySymbol}${Math.round(val * rate).toLocaleString()}`;
  const formatBillions = (val) => `${currencySymbol}${(val * rate).toFixed(1)}B`;
  
  // Revenue opportunity calculations
  const potentialPenetration = 35; // Target 35% penetration
  const newCustomers = ins.insuredVehicles * ((potentialPenetration - extWarranty.penetration) / 100);
  const avgWarrantyValue = warranty.annualCostUSD * warranty.typicalLoanTenure;
  const revenueOpportunity = (newCustomers * avgWarrantyValue) / 1000; // In millions

  return (
    <div className="mb-12" data-testid="extended-warranty-insurance">
      {/* Hero Banner */}
      <div className="p-8 rounded-3xl bg-gradient-to-br from-amber-50 via-orange-50 to-purple-50 border border-amber-200 shadow-lg mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div>
            <Badge className="bg-amber-100 text-amber-700 border-amber-200 mb-3">Revenue Growth Opportunity</Badge>
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
              Insurance + Extended Warranty Bundle
            </h3>
            <p className="text-lg text-slate-600 max-w-2xl">
              Partner with Wisedrive to offer <strong className="text-amber-600">complete vehicle protection</strong>: 
              Your insurance covers accidents, our warranty covers manufacturing defects. 
              One customer relationship, <strong className="text-amber-600">two revenue streams</strong>.
            </p>
          </div>
          <div className="flex-shrink-0">
            <div className="w-32 h-32 rounded-3xl bg-gradient-to-br from-amber-500 to-purple-600 flex items-center justify-center shadow-2xl shadow-amber-500/30">
              <Gift size={64} className="text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* The Complete Protection Story */}
      <div className="text-center mb-8">
        <h4 className="text-xl font-bold text-slate-900 mb-2">Complete Vehicle Protection for Your Customers</h4>
        <p className="text-slate-600">Fill the gap that insurance doesn't cover</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {/* Insurance Coverage */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-white border border-blue-200 shadow-lg">
          <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center mb-4">
            <Shield size={28} className="text-blue-600" />
          </div>
          <h5 className="text-lg font-bold text-slate-900 mb-2">Insurance Covers</h5>
          <ul className="space-y-2">
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <CheckCircle2 size={14} className="text-blue-600 mt-0.5" />
              Accidents & Collisions
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <CheckCircle2 size={14} className="text-blue-600 mt-0.5" />
              Theft & Vandalism
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <CheckCircle2 size={14} className="text-blue-600 mt-0.5" />
              Third-Party Liability
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <CheckCircle2 size={14} className="text-blue-600 mt-0.5" />
              Natural Disasters
            </li>
          </ul>
        </div>

        {/* The Gap */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-red-50 to-white border border-red-200 shadow-lg">
          <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center mb-4">
            <AlertOctagon size={28} className="text-red-500" />
          </div>
          <h5 className="text-lg font-bold text-slate-900 mb-2">Insurance DOESN'T Cover</h5>
          <ul className="space-y-2">
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <XCircle size={14} className="text-red-500 mt-0.5" />
              Engine Failures (wear & tear)
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <XCircle size={14} className="text-red-500 mt-0.5" />
              Transmission Issues
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <XCircle size={14} className="text-red-500 mt-0.5" />
              Electrical System Failures
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <XCircle size={14} className="text-red-500 mt-0.5" />
              Manufacturing Defects
            </li>
          </ul>
        </div>

        {/* Wisedrive Covers */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 shadow-lg">
          <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mb-4">
            <Wrench size={28} className="text-emerald-600" />
          </div>
          <h5 className="text-lg font-bold text-slate-900 mb-2">Wisedrive Warranty Covers</h5>
          <ul className="space-y-2">
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <CheckCircle2 size={14} className="text-emerald-600 mt-0.5" />
              Engine Repairs
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <CheckCircle2 size={14} className="text-emerald-600 mt-0.5" />
              Transmission Repairs
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <CheckCircle2 size={14} className="text-emerald-600 mt-0.5" />
              Electrical System Fixes
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <CheckCircle2 size={14} className="text-emerald-600 mt-0.5" />
              All Manufacturing Defects
            </li>
          </ul>
        </div>
      </div>

      {/* Revenue Opportunity */}
      <div className="p-6 rounded-2xl bg-gradient-to-r from-emerald-50 to-amber-50 border border-emerald-200 shadow-lg mb-8">
        <div className="grid md:grid-cols-4 gap-6 text-center">
          <div>
            <div className="text-3xl font-bold text-slate-900">{extWarranty.penetration}%</div>
            <div className="text-xs text-slate-600">Current Warranty Penetration</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-emerald-600">35%</div>
            <div className="text-xs text-slate-600">Target with Insurance Bundle</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-amber-600">{newCustomers.toFixed(1)}M</div>
            <div className="text-xs text-slate-600">New Warranty Customers</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-purple-600">{formatBillions(revenueOpportunity / 1000)}</div>
            <div className="text-xs text-slate-600">Revenue Opportunity</div>
          </div>
        </div>
      </div>

      {/* Operational Synergy */}
      <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
            <Cog size={20} className="text-purple-600" />
          </div>
          <h4 className="font-bold text-slate-900">Operational Synergy</h4>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
            <div className="text-sm text-purple-700 font-semibold mb-1">Same Garage Network</div>
            <p className="text-xs text-slate-600">Wisedrive uses your existing authorized service centers for warranty claims</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
            <div className="text-sm text-purple-700 font-semibold mb-1">Single Customer Touchpoint</div>
            <p className="text-xs text-slate-600">Customer buys insurance + warranty together, simplifying their experience</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
            <div className="text-sm text-purple-700 font-semibold mb-1">Commission Revenue</div>
            <p className="text-xs text-slate-600">Earn commission on every extended warranty sold through your platform</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const InsuranceROI = ({ country, showLocalCurrency }) => {
  const data = COUNTRY_DATA[country];
  const ins = data.insurance;
  
  const currencySymbol = showLocalCurrency ? data.currencySymbol : '$';
  const rate = showLocalCurrency ? data.exchangeRate : 1;
  const formatMillions = (val) => `${currencySymbol}${Math.round(val * rate).toLocaleString()}M`;

  const fraudSavings = ins.fraudExposure * 0.4;
  const claimsSavings = ins.motorInsuranceMarket * 1000 * 0.03;
  const warrantyCommission = ins.insuredVehicles * 0.1 * data.warrantyPricing.annualCostUSD * 0.15;

  return (
    <div className="mb-12 p-8 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 shadow-2xl" data-testid="insurance-roi">
      <div className="text-center mb-8">
        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mb-4">Business Case</Badge>
        <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
          ROI for Insurance Partners
        </h3>
        <p className="text-slate-400">Tangible value from day one</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700 text-center hover:border-emerald-500/50 transition-colors">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/20">
            <Shield size={32} className="text-white" />
          </div>
          <div className="text-4xl font-bold text-emerald-400 mb-2">{formatMillions(fraudSavings)}</div>
          <div className="text-sm text-white mb-2">Fraud Prevention Savings</div>
          <p className="text-xs text-slate-400">40% reduction in fraudulent claims through AI detection</p>
        </div>

        <div className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700 text-center hover:border-emerald-500/50 transition-colors">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/20">
            <FileCheck size={32} className="text-white" />
          </div>
          <div className="text-4xl font-bold text-emerald-400 mb-2">{formatMillions(claimsSavings)}</div>
          <div className="text-sm text-white mb-2">Claims Optimization</div>
          <p className="text-xs text-slate-400">3% reduction in claim payouts through accurate assessment</p>
        </div>

        <div className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700 text-center hover:border-emerald-500/50 transition-colors">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/20">
            <Banknote size={32} className="text-white" />
          </div>
          <div className="text-4xl font-bold text-emerald-400 mb-2">{formatMillions(warrantyCommission)}</div>
          <div className="text-sm text-white mb-2">Warranty Commission</div>
          <p className="text-xs text-slate-400">15% commission on extended warranty sales</p>
        </div>
      </div>
    </div>
  );
};

const InsuranceSection = ({ country, showLocalCurrency }) => {
  return (
    <div data-testid="insurance-section">
      <InsuranceMarketIntelligence country={country} showLocalCurrency={showLocalCurrency} />
      <AITechnologyShowcase country={country} showLocalCurrency={showLocalCurrency} />
      <InsuranceInspectionComparison country={country} showLocalCurrency={showLocalCurrency} />
      <InsuranceClaimsOptimization country={country} showLocalCurrency={showLocalCurrency} />
      <GenZBuyingTrends industry="insurance" />
      <ExtendedWarrantyPartnershipInsurance country={country} showLocalCurrency={showLocalCurrency} />
      <InsuranceROI country={country} showLocalCurrency={showLocalCurrency} />

      {/* CTA Section - Dark for insurance impact */}
      <div className="p-8 rounded-3xl bg-gradient-to-r from-slate-900 to-slate-800 text-center shadow-2xl border border-slate-700">
        <h4 className="text-2xl font-bold text-white mb-3">Transform Your Insurance Operations</h4>
        <p className="text-slate-300 mb-6 max-w-xl mx-auto">See how AI-powered inspections can reduce fraud and increase revenue</p>
        <Button 
          size="lg"
          className="bg-emerald-500 hover:bg-emerald-600 text-white px-10 h-14 text-lg gap-2 rounded-full shadow-lg font-bold"
          data-testid="insurance-demo-btn"
        >
          <Play size={20} />
          Request Insurance Partnership Demo
        </Button>
      </div>
    </div>
  );
};

const AuctionSection = ({ country }) => {
  return (
    <div className="text-center py-20" data-testid="auction-section">
      <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-4">Coming Soon</Badge>
      <h3 className="text-2xl font-bold text-white mb-4">Auction House Solutions</h3>
      <p className="text-slate-400">Auction-specific use cases will be enhanced.</p>
    </div>
  );
};

// ============ MAIN COMPONENT ============

export default function B2BShowcasePage() {
  const [activeTab, setActiveTab] = useState("banks");
  const [selectedCountry, setSelectedCountry] = useState("india");
  const [showLocalCurrency, setShowLocalCurrency] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Top Bar with Country Selector */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <a 
            href="/"
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#003399] transition-colors font-medium"
            data-testid="back-to-portal"
          >
            <ChevronRight size={16} className="rotate-180" />
            Back to Portal
          </a>
          <CountrySelector 
            selectedCountry={selectedCountry} 
            onCountryChange={setSelectedCountry}
            showLocalCurrency={showLocalCurrency}
            onCurrencyToggle={setShowLocalCurrency}
          />
        </div>
      </div>

      {/* Hero Section - Light Theme Sales Focused */}
      <section className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-purple-50/20">
        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#003399]/5 rounded-full blur-3xl" />
          <div className="absolute top-60 -left-20 w-60 h-60 bg-purple-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/3 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Main Message */}
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#003399] to-[#0055DD] flex items-center justify-center shadow-xl shadow-[#003399]/20">
                  <Zap size={28} className="text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#003399]">Enterprise B2B</span>
                  <span className="text-sm text-slate-500">AI-Powered Vehicle Intelligence</span>
                </div>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-[1.1] tracking-tight">
                Stop Losing Money on
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">
                  Bad Vehicle Data
                </span>
              </h1>

              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Banks lose <span className="text-red-500 font-bold">$2.3B</span> annually to overvalued collateral. 
                Insurers pay <span className="text-red-500 font-bold">$3B+</span> in fraudulent claims. 
                <strong className="text-slate-900"> Our AI changes that.</strong>
              </p>

              <div className="flex flex-wrap gap-4 mb-10">
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-[#003399] to-[#0055DD] hover:from-[#002277] hover:to-[#0044CC] text-white px-8 h-14 text-lg gap-2 rounded-full shadow-xl shadow-[#003399]/30 border-0"
                  data-testid="request-demo-btn"
                >
                  <Sparkles size={20} />
                  Get Your ROI Analysis
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-slate-300 text-slate-700 hover:bg-slate-100 hover:border-slate-400 px-8 h-14 text-lg gap-2 rounded-full"
                  data-testid="contact-sales-btn"
                >
                  <Phone size={20} />
                  Talk to Sales
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center gap-8">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 size={18} className="text-emerald-600" />
                  </div>
                  <span className="text-slate-600 font-medium">95%+ Detection Accuracy</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <Clock size={18} className="text-blue-600" />
                  </div>
                  <span className="text-slate-600 font-medium">30-Second Analysis</span>
                </div>
              </div>
            </div>

            {/* Right: Key Metrics Card */}
            <div className="lg:pl-8">
              <div className="relative p-8 rounded-3xl bg-white border border-slate-200 shadow-2xl shadow-slate-200/50">
                {/* Accent Corner */}
                <div className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/30 rotate-12">
                  <TrendingUp size={36} className="text-white -rotate-12" />
                </div>
                
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-3xl">{COUNTRY_DATA[selectedCountry].flag}</span>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{COUNTRY_DATA[selectedCountry].name}</h3>
                    <span className="text-sm text-slate-500">Market Opportunity</span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-gradient-to-r from-red-50 to-white border border-red-100">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 font-medium">Annual Fraud Exposure</span>
                      <span className="text-2xl font-bold text-red-500">
                        ${(COUNTRY_DATA[selectedCountry].insurance.fraudExposure / 1000).toFixed(1)}B
                      </span>
                    </div>
                  </div>

                  <div className="p-4 rounded-xl bg-gradient-to-r from-amber-50 to-white border border-amber-100">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 font-medium">NPL Risk (Used Cars)</span>
                      <span className="text-2xl font-bold text-amber-500">
                        {COUNTRY_DATA[selectedCountry].market.nplRate}%
                      </span>
                    </div>
                  </div>

                  <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-50 to-white border border-emerald-100">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 font-medium">Wisedrive Impact</span>
                      <span className="text-2xl font-bold text-emerald-500">
                        40% Less Risk
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-slate-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500 font-medium">Potential Annual Savings</span>
                    <span className="text-2xl font-bold text-[#003399]">
                      ${((COUNTRY_DATA[selectedCountry].insurance.fraudExposure * 0.4 + COUNTRY_DATA[selectedCountry].market.totalNPLExposure * 0.35) / 1000).toFixed(1)}B+
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Tabs Section - Light Theme */}
      <section className="relative py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 rounded-full bg-[#003399]/10 text-[#003399] text-xs font-bold uppercase tracking-wider mb-4">
              Solutions That Deliver ROI
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Choose Your Industry
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              See exactly how Wisedrive AI protects your portfolio and drives revenue growth
            </p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full max-w-lg mx-auto grid grid-cols-2 h-auto bg-slate-100 p-1.5 rounded-full border border-slate-200 shadow-sm mb-12">
              <TabsTrigger
                value="banks"
                className="rounded-full px-6 py-3.5 text-sm font-semibold transition-all flex items-center justify-center gap-2.5 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-md data-[state=inactive]:bg-transparent data-[state=inactive]:text-slate-500 data-[state=inactive]:shadow-none data-[state=inactive]:hover:text-slate-700"
                data-testid="tab-banks"
              >
                <Building2 size={18} />
                Banks & NBFCs
              </TabsTrigger>
              <TabsTrigger
                value="insurance"
                className="rounded-full px-6 py-3.5 text-sm font-semibold transition-all flex items-center justify-center gap-2.5 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-md data-[state=inactive]:bg-transparent data-[state=inactive]:text-slate-500 data-[state=inactive]:shadow-none data-[state=inactive]:hover:text-slate-700"
                data-testid="tab-insurance"
              >
                <Shield size={18} />
                Insurance
              </TabsTrigger>
            </TabsList>

            <TabsContent value="banks" className="mt-0">
              <BanksSection country={selectedCountry} showLocalCurrency={showLocalCurrency} />
            </TabsContent>

            <TabsContent value="insurance" className="mt-0">
              <InsuranceSection country={selectedCountry} showLocalCurrency={showLocalCurrency} />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Footer - Light */}
      <footer className="py-12 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-slate-500">
            © 2026 Wisedrive Technologies. All rights reserved. | Enterprise Solutions
          </p>
        </div>
      </footer>
    </div>
  );
}
